import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import mlLogo from "../assets/mercadolibre.png";
import tnLogo from "../assets/tiendanube.png";
import shLogo from "../assets/shopify.png";
import wcLogo from "../assets/woocommerce.png";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";
import StoreScheduleEditor from "../components/stores/StoreScheduleEditor";
import StoreScheduleSummary from "../components/stores/StoreScheduleSummary";
import {
  createEmptyGroupedSchedule,
  cutoffsToGroupedSchedule,
  fetchStoreCutoffs,
  groupedScheduleToPayload,
  saveStoreCutoffs,
} from "../components/stores/scheduleUtils";
import { GroupedWeeklySchedule } from "../components/stores/types";
import { useLeaflet } from "../hooks/useLeaflet";
import { buildGeocodeQuery, GeocodeCandidate } from "../components/warehouse/geoQuery";

/* ===== ML OAuth (BACKEND) ===== */
type MercadoLibreLinkResponse = {
  ok?: boolean;
  url?: string;
  link?: string;
  error?: string;
  message?: string;
  data?: {
    url?: string;
    link?: string;
  };
  result?: {
    url?: string;
    link?: string;
  };
};

function pickMercadoLibreLink(payload: MercadoLibreLinkResponse) {
  return (
    payload?.url ||
    payload?.link ||
    payload?.data?.url ||
    payload?.data?.link ||
    payload?.result?.url ||
    payload?.result?.link ||
    ""
  );
}

async function requestMercadoLibreLink({
  token,
  accountId,
}: {
  token: string | null;
  accountId: number | string;
}): Promise<string> {
  const response = await authFetch("/auth/mercadolibre/link", {
    method: "POST",
    token,
    json: true,
    body: JSON.stringify({ id: Number(accountId) }),
  });

  const json: MercadoLibreLinkResponse = await response.json().catch(() => ({}));
  const url = pickMercadoLibreLink(json);

  if (!response.ok || json?.ok === false || !url) {
    throw new Error(
      friendlyMessage(
        bestError(response, json as Record<string, unknown>, ""),
        "No se pudo generar el link de Mercado Libre"
      )
    );
  }

  return url;
}

const PROVIDERS = [
  { value: "mercadolibre", label: "Mercado Libre" },
  { value: "tiendanube", label: "Tienda Nube" },
  { value: "shopify", label: "Shopify" },
  { value: "woocommerce", label: "WooCommerce" },
  { value: "otros", label: "Otros" },
] as const;

/* ===== Types ===== */
type ProviderValue = (typeof PROVIDERS)[number]["value"];

type StoreApiRaw = Record<string, unknown>;

type UserApiRow = {
  id?: number;
  username?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  rol?: string;
};

type StoreItem = {
  id: number | string | null;
  slug: string;
  name: string;
  business_name: string;
  tarifaID: number | string | null;
  externalToken: string;
  address: string;
  postalCode: string;
  active: boolean;
  // Tiene envíos Turbo: solo decide si se dibuja en Turbo · En vivo.
  turboEnabled: boolean;
  createdAt: string | null;
  updatedAt: string | null;
  latitude: number | null;
  longitude: number | null;
  geoConfirmedAt: string | null;
  geoConfirmedBy: string | null;
  geoConfirmedRole: string | null;
  geoProposedLatitude: number | null;
  geoProposedLongitude: number | null;
  geoProposedAt: string | null;
  geoProposedBy: string | null;
};

type SellerOption = {
  id: number;
  userID: number | null;
  label: string;
  fullName: string;
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  phone: string;
  razonSocial: string;
};

type LinkedSeller = {
  id: number;
  userID: number | null;
  razon_social?: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  email?: string;
  phone?: string;
};

type AccountItem = {
  id: number;
  provider?: string;
  display_name?: string;
  displayName?: string;
  account_identifier?: string;
  accountIdentifier?: string;
  ml_user_id?: number | null;
  mlUserId?: number | null;
  auto_entry?: boolean | null;
  autoEntry?: boolean | null;
  __hasOAuthToken?: boolean;
  __tokenMlUserId?: number | null;
  __resolvedProvider?: string;
};

type StoreTariffOption = {
  id: number;
  name: string;
  type: string;
  price_caba?: number | string | null;
  price_gba1?: number | string | null;
  price_gba2?: number | string | null;
  price_gba3?: number | string | null;
};

type TokenRecord = {
  id?: number;
  account_id?: number;
  user_id?: number | string | null;
  // El servidor ya no manda los tokens: solo si están los dos.
  linked?: boolean | null;
  expires_at?: number | string | null;
};

type StoresResponse = {
  ok?: boolean;
  error?: string;
  stores?: StoreApiRaw[];
  data?: StoreApiRaw[];
  result?: StoreApiRaw[];
};

type SellersResponse = {
  ok?: boolean;
  error?: string;
  sellers?: Array<Record<string, unknown>>;
  data?: Array<Record<string, unknown>>;
  result?: Array<Record<string, unknown>>;
};

type UsersResponse = {
  ok?: boolean;
  error?: string;
  usuarios?: UserApiRow[];
  data?: UserApiRow[];
  result?: UserApiRow[];
};

type AccountsResponse = {
  ok?: boolean;
  error?: string;
  accounts?: AccountItem[];
  data?: AccountItem[];
  result?: AccountItem[];
};

type TariffsResponse = {
  ok?: boolean;
  error?: string;
  tariffs?: StoreApiRaw[];
  data?: StoreApiRaw[] | StoreApiRaw;
  result?: StoreApiRaw[] | StoreApiRaw;
  tariff?: StoreApiRaw;
};

type ExternalTokenResponse = {
  ok?: boolean;
  error?: string;
  message?: string;
  token?: string;
  external_token?: string;
  externalToken?: string;
  data?: string | Record<string, unknown>;
  result?: string | Record<string, unknown>;
};

type LinkedSellersResponse = {
  ok?: boolean;
  error?: string;
  sellers?: LinkedSeller[];
  seller?: LinkedSeller;
  data?: LinkedSeller[] | LinkedSeller;
  result?: LinkedSeller[] | LinkedSeller;
};

type TokensResponse = {
  ok?: boolean;
  error?: string;
  tokens?: TokenRecord | TokenRecord[] | null;
  data?: TokenRecord | TokenRecord[] | null;
  result?: TokenRecord | TokenRecord[] | null;
};

type CreateAccountResponse = {
  ok?: boolean;
  error?: string;
  message?: string;
  accountID?: number | string;
  accountId?: number | string;
  id?: number | string;
  data?: {
    id?: number | string;
    accountID?: number | string;
    accountId?: number | string;
  };
  result?: {
    id?: number | string;
    accountID?: number | string;
    accountId?: number | string;
  };
};

type GenericResponse = {
  ok?: boolean;
  error?: string;
  message?: string;
};

type ModalProps = {
  title: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  width?: string;
};

type IconBtnProps = {
  title: string;
  onClick: (e?: React.MouseEvent) => void;
  children: React.ReactNode;
  className?: string;
};

type TabBtnProps = {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
};

type CreateStoreModalProps = {
  token: string | null;
  storeTariffs: StoreTariffOption[];
  onClose: () => void;
  onCreated?: () => Promise<void> | void;
};

type ViewStoreModalProps = {
  token: string | null;
  store: StoreItem;
  allSellers: SellerOption[];
  storeTariffs: StoreTariffOption[];
  onClose: () => void;
  onEdit: () => void;
  onChanged?: () => Promise<void> | void;
};

type EditStoreModalProps = {
  token: string | null;
  store: StoreItem;
  allSellers: SellerOption[];
  storeTariffs: StoreTariffOption[];
  onClose: () => void;
  onChanged?: () => Promise<void> | void;
};

type AccountDetailModalProps = {
  storeId: number | string;
  account: AccountItem;
  onClose: () => void;
  token: string | null;
  onUpdated?: () => Promise<void> | void;
  // Lo que se refresca cuando el cliente acepta el «Link de vinculación».
  // Sin esto se usa onUpdated.
  onLinked?: () => Promise<void> | void;
};

/* ===== Logos ===== */
const LOGO_BY_PROVIDER: Record<string, string | undefined> = {
  mercadolibre: mlLogo,
  tiendanube: tnLogo,
  shopify: shLogo,
  woocommerce: wcLogo,
};

const pageWrapStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "20px",
};

const headerRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "16px",
  flexWrap: "wrap",
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "34px",
  fontWeight: 800,
  color: "var(--text-primary)",
  letterSpacing: "-0.03em",
};

const subtitleStyle: React.CSSProperties = {
  margin: "8px 0 0 0",
  color: "var(--text-secondary)",
  fontSize: "15px",
  lineHeight: 1.6,
};

const actionsWrapStyle: React.CSSProperties = {
  display: "flex",
  gap: "12px",
  flexWrap: "wrap",
};

const statsGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: "16px",
};

const kpiLabelStyle: React.CSSProperties = {
  fontSize: "13px",
  color: "var(--text-secondary)",
  marginBottom: "10px",
};

const kpiValueStyle: React.CSSProperties = {
  fontSize: "28px",
  fontWeight: 800,
  color: "var(--text-primary)",
  letterSpacing: "-0.03em",
};

const kpiMetaStyle: React.CSSProperties = {
  marginTop: "10px",
  fontSize: "13px",
  color: "var(--text-secondary)",
};

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "18px",
  fontWeight: 700,
  color: "var(--text-primary)",
};

const sectionTextStyle: React.CSSProperties = {
  margin: "6px 0 0 0",
  color: "var(--text-secondary)",
  fontSize: "14px",
};

const tableWrapStyle: React.CSSProperties = {
  overflowX: "auto",
  border: "1px solid var(--border)",
  borderRadius: "18px",
  background: "rgba(255,255,255,0.02)",
};

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
};

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "14px 12px",
  color: "var(--text-secondary)",
  fontSize: "13px",
  fontWeight: 700,
  borderBottom: "1px solid var(--border)",
  background: "rgba(255,255,255,0.02)",
};

const tdStyle: React.CSSProperties = {
  padding: "14px 12px",
  color: "var(--text-primary)",
  fontSize: "14px",
  borderBottom: "1px solid var(--border)",
  verticalAlign: "middle",
};

const iconBtnStyle: React.CSSProperties = {
  background: "transparent",
  border: "1px solid var(--border)",
  borderRadius: "12px",
  minWidth: "38px",
  minHeight: "38px",
  cursor: "pointer",
  color: "var(--text-primary)",
  fontSize: "16px",
};

const modalOverlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 100,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const modalBackdropStyle: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  background: "rgba(0,0,0,0.6)",
};

const modalHeaderStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "16px 18px",
  borderBottom: "1px solid var(--border)",
};

const modalBodyStyle: React.CSSProperties = {
  padding: "18px",
  overflowY: "auto",
  maxHeight: "72vh",
};

const smallMutedStyle: React.CSSProperties = {
  fontSize: "12px",
  color: "var(--text-secondary)",
};

const providerCardStyle: React.CSSProperties = {
  border: "1px solid var(--border)",
  borderRadius: "18px",
  background: "rgba(255,255,255,0.03)",
  padding: "14px",
};

const contactCardStyle: React.CSSProperties = {
  border: "1px solid var(--border)",
  borderRadius: "18px",
  background: "rgba(255,255,255,0.03)",
  padding: "16px",
  display: "flex",
  flexDirection: "column",
  gap: "10px",
};

const selectStyle: React.CSSProperties = {
  width: "100%",
  minHeight: "48px",
  padding: "0 14px",
  borderRadius: "16px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  color: "var(--text-primary)",
};

const switchStyle: React.CSSProperties = {
  position: "relative",
  width: "52px",
  height: "30px",
  borderRadius: "999px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  cursor: "pointer",
};

function AccountLogo({
  provider,
  className = "h-5 w-auto",
}: {
  provider?: string;
  className?: string;
}) {
  const src = LOGO_BY_PROVIDER[String(provider || "").toLowerCase()];
  if (!src) {
    return (
      <span style={{ fontSize: "12px", textTransform: "capitalize" }}>
        {provider || "—"}
      </span>
    );
  }
  return <img src={src} alt={provider} className={className} />;
}

/* ===== UI ===== */
const IconBtn = ({ title, onClick, children, className = "" }: IconBtnProps) => (
  <button
    type="button"
    title={title}
    onClick={onClick}
    className={className}
    style={iconBtnStyle}
  >
    {children}
  </button>
);

const Modal = ({ title, onClose, children, width = "1100px" }: ModalProps) => (
  <div style={modalOverlayStyle}>
    <div style={modalBackdropStyle} onClick={onClose} />
    <div
      style={{
        position: "relative",
        width: "95vw",
        maxWidth: width,
        maxHeight: "85vh",
        overflow: "hidden",
      }}
    >
      <Card style={{ padding: 0 }}>
        <div style={modalHeaderStyle}>
          <h3
            style={{
              margin: 0,
              fontSize: "18px",
              fontWeight: 700,
              color: "var(--text-primary)",
            }}
          >
            {title}
          </h3>

          <button
            onClick={onClose}
            aria-label="Cerrar"
            style={{
              background: "transparent",
              border: "none",
              color: "var(--text-secondary)",
              cursor: "pointer",
              fontSize: "22px",
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        <div style={modalBodyStyle}>{children}</div>
      </Card>
    </div>
  </div>
);

const StatusBadge = ({ active }: { active: boolean }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      padding: "5px 10px",
      borderRadius: "999px",
      fontSize: "12px",
      fontWeight: 700,
      letterSpacing: "0.02em",
      background: active ? "rgba(107,162,125,0.14)" : "rgba(185,100,95,0.14)",
      color: active ? "var(--success)" : "var(--danger)",
      border: `1px solid ${active ? "rgba(107,162,125,0.24)" : "rgba(185,100,95,0.24)"}`,
    }}
  >
    {active ? "ACTIVA" : "INACTIVA"}
  </span>
);

const TabBtn = ({ active, onClick, children }: TabBtnProps) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      minHeight: "42px",
      padding: "0 16px",
      borderRadius: "999px",
      border: active ? "1px solid var(--border)" : "1px solid transparent",
      background: active ? "var(--primary-soft)" : "transparent",
      color: active ? "var(--text-primary)" : "var(--text-secondary)",
      fontSize: "14px",
      fontWeight: active ? 700 : 600,
      cursor: "pointer",
    }}
  >
    {children}
  </button>
);

/* ===== Helpers ===== */
const pickStoresArray = (payload: StoresResponse) =>
  payload?.stores || payload?.data || payload?.result || [];

const pickSellersArray = (payload: SellersResponse) =>
  payload?.sellers || payload?.data || payload?.result || [];

const pickUsersArray = (payload: UsersResponse) =>
  payload?.usuarios || payload?.data || payload?.result || [];

const pickAccountsArray = (payload: AccountsResponse) =>
  payload?.accounts || payload?.data || payload?.result || [];

const pickTariffsArray = (payload: TariffsResponse): StoreApiRaw[] => {
  if (Array.isArray(payload?.tariffs)) return payload.tariffs;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.result)) return payload.result;
  if (payload?.tariff && typeof payload.tariff === "object") return [payload.tariff];
  if (payload?.data && !Array.isArray(payload.data) && typeof payload.data === "object") {
    return [payload.data];
  }
  if (payload?.result && !Array.isArray(payload.result) && typeof payload.result === "object") {
    return [payload.result];
  }
  return [];
};

const pickLinkedSellersArray = (payload: LinkedSellersResponse): LinkedSeller[] => {
  if (Array.isArray(payload?.sellers)) return payload.sellers;
  if (payload?.seller && typeof payload.seller === "object") return [payload.seller];
  if (Array.isArray(payload?.data)) return payload.data;
  if (payload?.data && !Array.isArray(payload.data)) return [payload.data];
  if (Array.isArray(payload?.result)) return payload.result;
  if (payload?.result && !Array.isArray(payload.result)) return [payload.result];
  return [];
};

const pickTokenRecord = (payload: TokensResponse): TokenRecord | null => {
  if (payload?.tokens && !Array.isArray(payload.tokens)) return payload.tokens;
  if (Array.isArray(payload?.tokens)) return payload.tokens[0] || null;
  if (payload?.data && !Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload?.data)) return payload.data[0] || null;
  if (payload?.result && !Array.isArray(payload.result)) return payload.result;
  if (Array.isArray(payload?.result)) return payload.result[0] || null;
  return null;
};

const hasValidMlToken = (tokenRecord: TokenRecord | null) => {
  if (!tokenRecord) return false;
  return tokenRecord.linked === true;
};

const normalizeStore = (x: StoreApiRaw): StoreItem => {
  const id = (x?.id as number | string | null | undefined) ?? null;

  const name =
    (x?.nombre_fantasia as string | undefined) ??
    (x?.nombreFantasia as string | undefined) ??
    (x?.name as string | undefined) ??
    (x?.store_name as string | undefined);

  const categoria =
    (x?.categoria as string | undefined) ??
    (x?.business_name as string | undefined) ??
    (x?.businessName as string | undefined);

  const tarifaID =
    (x?.tarifa_id as number | string | null | undefined) ??
    (x?.tarifaID as number | string | null | undefined) ??
    (x?.tariff_id as number | string | null | undefined) ??
    (x?.tariffID as number | string | null | undefined) ??
    null;

  const externalToken =
    (x?.external_token as string | undefined) ??
    (x?.externalToken as string | undefined) ??
    (x?.store_external_token as string | undefined) ??
    (x?.api_token as string | undefined) ??
    (x?.apiToken as string | undefined) ??
    "";

  const address = (x?.direccion as string | undefined) ?? "";
  const postalCode = (x?.codigo_postal as string | undefined) ?? "";
  const active = (x?.active as boolean | undefined) ?? true;
  const turboEnabled = x?.turbo_enabled === true;
  const createdAt = (x?.created_at as string | undefined) ?? null;
  const updatedAt = (x?.updated_at as string | undefined) ?? null;

  const toNum = (v: unknown): number | null => {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  };

  const toStr = (v: unknown): string | null =>
    v === null || v === undefined || v === "" ? null : String(v);

  return {
    id,
    slug: id != null ? String(id) : "—",
    name: name ?? "—",
    business_name: categoria ?? "—",
    tarifaID,
    externalToken,
    address,
    postalCode,
    active,
    turboEnabled,
    createdAt,
    updatedAt,
    latitude: toNum(x?.latitude),
    longitude: toNum(x?.longitude),
    geoConfirmedAt: toStr(x?.geo_confirmed_at),
    geoConfirmedBy: toStr(x?.geo_confirmed_by),
    geoConfirmedRole: toStr(x?.geo_confirmed_role),
    geoProposedLatitude: toNum(x?.geo_proposed_latitude),
    geoProposedLongitude: toNum(x?.geo_proposed_longitude),
    geoProposedAt: toStr(x?.geo_proposed_at),
    geoProposedBy: toStr(x?.geo_proposed_by),
  };
};

const normalizeStoreTariff = (row: StoreApiRaw): StoreTariffOption | null => {
  const rawId = row?.id as number | string | undefined;
  if (rawId === undefined || rawId === null || rawId === "") return null;

  const type = String((row?.type as string | undefined) || "").trim();
  const normalizedType = type.toUpperCase();
  if (normalizedType && normalizedType !== "STORE") return null;

  return {
    id: Number(rawId),
    name: String((row?.name as string | undefined) || `Tarifa ${rawId}`),
    type: normalizedType || "STORE",
    price_caba: row?.price_caba as number | string | null | undefined,
    price_gba1: row?.price_gba1 as number | string | null | undefined,
    price_gba2: row?.price_gba2 as number | string | null | undefined,
    price_gba3: row?.price_gba3 as number | string | null | undefined,
  };
};

const formatTariffOptionLabel = (tariff: StoreTariffOption) => {
  const prices = [
    tariff.price_caba != null ? `CABA $${tariff.price_caba}` : "",
    tariff.price_gba1 != null ? `GBA1 $${tariff.price_gba1}` : "",
    tariff.price_gba2 != null ? `GBA2 $${tariff.price_gba2}` : "",
    tariff.price_gba3 != null ? `GBA3 $${tariff.price_gba3}` : "",
  ].filter(Boolean);

  return `${tariff.name} (ID ${tariff.id})${prices.length ? ` — ${prices.join(" · ")}` : ""}`;
};

const getTariffName = (tariffs: StoreTariffOption[], tariffID: number | string | null) => {
  if (tariffID === null || tariffID === undefined || tariffID === "") return "—";
  const found = tariffs.find((tariff) => String(tariff.id) === String(tariffID));
  return found ? `${found.name} (ID ${found.id})` : `Tarifa ID ${tariffID}`;
};

// Forma de pago (legacy_store_financial_config.forma_pago) — mismo patrón
// que descuento: vive en la misma tabla, se edita/consulta vía los mismos
// endpoints de /v1/liquidaciones/config/stores.
const FORMA_PAGO_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "EFECTIVO", label: "Efectivo" },
  { value: "TRANSFERENCIA_SIN_FACTURA", label: "Transferencia sin factura" },
  { value: "TRANSFERENCIA_FACTURA_C", label: "Transferencia Factura C" },
  { value: "TRANSFERENCIA_FACTURA_A", label: "Transferencia Factura A" },
];
const formaPagoLabel = (value: string | null | undefined): string =>
  FORMA_PAGO_OPTIONS.find((opt) => opt.value === value)?.label ?? "—";

const pickExternalToken = (payload: ExternalTokenResponse): string => {
  const direct = payload?.token || payload?.external_token || payload?.externalToken;
  if (direct) return String(direct);

  const data = payload?.data;
  if (typeof data === "string") return data;
  if (data && typeof data === "object") {
    return String(
      data.token ||
        data.external_token ||
        data.externalToken ||
        data.api_token ||
        data.apiToken ||
        ""
    );
  }

  const result = payload?.result;
  if (typeof result === "string") return result;
  if (result && typeof result === "object") {
    return String(
      result.token ||
        result.external_token ||
        result.externalToken ||
        result.api_token ||
        result.apiToken ||
        ""
    );
  }

  return "";
};

async function generateExternalTokenForStore(
  token: string | null,
  storeId: number | string | null
): Promise<string> {
  if (!token || storeId === null || storeId === undefined || storeId === "") {
    throw new Error("No se pudo identificar la tienda para generar token externo.");
  }

  const response = await authFetch(`/v2/stores/${storeId}/external-token`, {
    method: "POST",
    token,
  });

  const json: ExternalTokenResponse = await response.json().catch(() => ({}));

  if (!response.ok || json?.ok === false) {
    throw new Error(
      friendlyMessage(
        bestError(response, json as Record<string, unknown>, ""),
        "No se pudo generar el token externo"
      )
    );
  }

  return pickExternalToken(json);
}


async function getExternalTokenForStore(
  token: string | null,
  storeId: number | string | null
): Promise<string> {
  if (!token || storeId === null || storeId === undefined || storeId === "") {
    return "";
  }

  const response = await authFetch(`/v2/stores/${storeId}/external-token`, {
    method: "GET",
    token,
  });

  const json: ExternalTokenResponse = await response.json().catch(() => ({}));

  if (!response.ok || json?.ok === false) {
    return "";
  }

  return pickExternalToken(json);
}

function buildFullName(firstName?: string, lastName?: string) {
  return [firstName || "", lastName || ""]
    .map((v) => String(v).trim())
    .filter(Boolean)
    .join(" ");
}

function normalizeSellerOptions(
  sellerRows: Array<Record<string, unknown>>,
  userRows: UserApiRow[]
): SellerOption[] {
  const userMap = new Map<number, UserApiRow>();

  (userRows || []).forEach((user) => {
    if (user?.id != null) {
      userMap.set(Number(user.id), user);
    }
  });

  return (sellerRows || [])
    .filter((x) => x?.id != null)
    .map((x) => {
      const sellerId = Number(x.id);
      const userID =
        x?.userID != null
          ? Number(x.userID)
          : x?.user_id != null
          ? Number(x.user_id)
          : null;

      const user = userID != null ? userMap.get(userID) : undefined;

      const firstName =
        (user?.first_name as string | undefined) ||
        (x?.first_name as string | undefined) ||
        (x?.firstName as string | undefined) ||
        "";

      const lastName =
        (user?.last_name as string | undefined) ||
        (x?.last_name as string | undefined) ||
        (x?.lastName as string | undefined) ||
        "";

      const fullName =
        buildFullName(firstName, lastName) ||
        (x?.full_name as string | undefined) ||
        (x?.fullName as string | undefined) ||
        "";

      const username =
        (user?.username as string | undefined) ||
        (x?.username as string | undefined) ||
        "";

      const email =
        (user?.email as string | undefined) ||
        (x?.email as string | undefined) ||
        "";

      const phone =
        (user?.phone as string | undefined) ||
        (x?.phone as string | undefined) ||
        "";

      const razonSocial =
        (x?.razon_social as string | undefined) ||
        (x?.razonSocial as string | undefined) ||
        "";

      const label = [fullName || username || `Seller ${sellerId}`, razonSocial]
        .filter(Boolean)
        .join(" — ");

      return {
        id: sellerId,
        userID,
        label,
        fullName,
        firstName,
        lastName,
        username,
        email,
        phone,
        razonSocial,
      };
    });
}

function enrichLinkedSellers(
  rows: LinkedSeller[],
  allSellers: SellerOption[]
): LinkedSeller[] {
  const sellerMap = new Map<number, SellerOption>();
  allSellers.forEach((seller) => {
    sellerMap.set(seller.id, seller);
  });

  return (rows || []).map((row) => {
    const extra = sellerMap.get(Number(row.id));

    return {
      ...row,
      userID: row.userID ?? extra?.userID ?? null,
      razon_social: row.razon_social || extra?.razonSocial || "",
      fullName: row.fullName || extra?.fullName || "",
      firstName: row.firstName || extra?.firstName || "",
      lastName: row.lastName || extra?.lastName || "",
      username: row.username || extra?.username || "",
      email: row.email || extra?.email || "",
      phone: row.phone || extra?.phone || "",
    };
  });
}

const normProvider = (p?: string) => String(p || "").toLowerCase();
const isMeli = (p?: string) => normProvider(p) === "mercadolibre";
const isTokenMeliCandidate = (tokenRecord: TokenRecord | null) =>
  Boolean(tokenRecord?.linked && tokenRecord?.user_id != null);

const getAccDisplayName = (a: AccountItem) =>
  a?.display_name ||
  a?.displayName ||
  a?.account_identifier ||
  a?.accountIdentifier ||
  "—";

const getAccIdentifier = (a: AccountItem) =>
  a?.account_identifier || a?.accountIdentifier || "—";

const getAccMlUserId = (a: AccountItem) => {
  const rawValue = a?.ml_user_id ?? a?.mlUserId ?? a?.__tokenMlUserId ?? null;

  if (rawValue == null) return null;

  const parsed = Number(rawValue);
  return Number.isNaN(parsed) ? null : parsed;
};

const getResolvedProvider = (a: AccountItem) => {
  if (a?.__resolvedProvider) return a.__resolvedProvider;
  if (a?.provider) return a.provider;
  if (a?.__hasOAuthToken && a?.__tokenMlUserId != null) return "Mercadolibre";
  return "";
};

const getAutoEntry = (a: AccountItem) => Boolean(a?.auto_entry ?? a?.autoEntry ?? false);

const isMeliLinked = (a: AccountItem) =>
  isMeli(getResolvedProvider(a)) &&
  getAccMlUserId(a) != null &&
  a.__hasOAuthToken === true;

async function readJsonSafe(resp: Response | null) {
  if (!resp) return {};
  if (resp.status === 204) return { ok: true };
  const ct = resp.headers?.get?.("content-type") || "";
  if (ct.includes("application/json")) {
    return resp.json().catch(() => ({}));
  }
  const txt = await resp.text().catch(() => "");
  return txt ? { ok: false, error: txt } : {};
}

function bestError(
  resp: Response | null,
  body: Record<string, unknown>,
  fallback = "Error"
) {
  if (typeof body?.error === "string") return body.error;
  if (typeof body?.message === "string") return body.message;
  if (resp?.status) return `${fallback} (HTTP ${resp.status})`;
  return fallback;
}

const RAW_DB_ERROR_PATTERNS = [
  /value too long/i,
  /duplicate key/i,
  /violates .* constraint/i,
  /null value in column/i,
  /invalid input syntax/i,
  /syntax error/i,
  /relation ".*" does not exist/i,
  /column ".*" does not exist/i,
  /character varying/i,
  /ECONNREFUSED/i,
  /ETIMEDOUT/i,
  /^error:\s/i,
];

/** Traduce errores crudos de Postgres/infra a un mensaje entendible para el usuario. */
function friendlyMessage(rawMessage: string | null | undefined, fallback: string): string {
  const msg = (rawMessage || "").trim();
  if (!msg) return fallback;

  if (RAW_DB_ERROR_PATTERNS.some((pattern) => pattern.test(msg))) {
    console.error("Error técnico (Stores):", msg);
    return fallback;
  }

  return msg;
}

function getGeoStatus(
  store: Pick<StoreItem, "latitude" | "longitude">
): { label: string; confirmed: boolean } {
  if (store.latitude != null && store.longitude != null) {
    return { label: "✅ Verificado", confirmed: true };
  }
  return { label: "⚠ Dirección pendiente de verificar", confirmed: false };
}

// Geolocalización de Stores: mismo flujo (buscar dirección -> pin en el
// mapa -> guardar) y mismo buscador que ya usa la resolución manual de
// Deliveries (ver GeoResolutionPanel.tsx) — reutiliza el endpoint genérico
// GET /v2/deliveries/geo/search (geocodifica un texto, no sabe nada de
// Delivery) y useLeaflet/buildGeocodeQuery tal cual están hoy. Lo único
// propio de Store es la confirmación (PUT /v2/stores/:id/location).
type StoreGeoConfidence = "high" | "medium" | "low";
type StoreGeoPin = { lat: number; lng: number; source: "auto" | "manual"; level?: StoreGeoConfidence };

const STORE_GEO_LEVEL_COLOR: Record<StoreGeoConfidence, string> = {
  high: "var(--success)",
  medium: "var(--warning, #d29922)",
  low: "var(--danger)",
};
const STORE_GEO_LEVEL_LABEL: Record<StoreGeoConfidence, string> = { high: "Alta", medium: "Media", low: "Baja" };
const STORE_GEO_MANUAL_COLOR = "var(--primary, #4f8cff)";
const STORE_GEO_CENTRO: [number, number] = [-34.62, -58.45];
const STORE_GEO_ZOOM_DEFAULT = 9;
const STORE_GEO_ZOOM_SELECCION = 16;

type StoreGeoLocationModalProps = {
  token: string;
  storeId: number | string;
  address: string;
  postalCode: string;
  onClose: () => void;
  onConfirmed: (coords: { latitude: number; longitude: number }) => void;
};

function StoreGeoLocationModal({
  token,
  storeId,
  address,
  postalCode,
  onClose,
  onConfirmed,
}: StoreGeoLocationModalProps) {
  const leaflet = useLeaflet();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);

  const [addrInput, setAddrInput] = useState(() =>
    buildGeocodeQuery({ direccion: address, codigoPostal: postalCode })
  );
  const [searchBusy, setSearchBusy] = useState(false);
  const [searchMsg, setSearchMsg] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<GeocodeCandidate[]>([]);
  const [pin, setPin] = useState<StoreGeoPin | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!leaflet || !containerRef.current || mapRef.current) return;
    const L = (window as any).L;
    const map = L.map(containerRef.current, { zoomControl: true }).setView(STORE_GEO_CENTRO, STORE_GEO_ZOOM_DEFAULT);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 18,
      attribution: "© OpenStreetMap",
    }).addTo(map);
    map.on("click", (e: any) => setPin({ lat: e.latlng.lat, lng: e.latlng.lng, source: "manual" }));
    mapRef.current = map;
  }, [leaflet]);

  useEffect(() => {
    const L = (window as any).L;
    const map = mapRef.current;
    if (!L || !map) return;

    if (!pin) {
      markerRef.current?.remove();
      markerRef.current = null;
      return;
    }

    const color = pin.source === "manual" ? STORE_GEO_MANUAL_COLOR : STORE_GEO_LEVEL_COLOR[pin.level ?? "low"];
    const icon = L.divIcon({
      className: "",
      html: `<div style="width:22px;height:22px;border-radius:50% 50% 50% 0;background:${color};border:2px solid #1a1a1a;transform:rotate(-45deg);box-shadow:0 1px 3px rgba(0,0,0,0.4);"></div>`,
      iconSize: [22, 22],
      iconAnchor: [11, 22],
    });

    if (!markerRef.current) {
      const marker = L.marker([pin.lat, pin.lng], { icon, draggable: true }).addTo(map);
      marker.on("dragend", () => {
        const ll = marker.getLatLng();
        setPin({ lat: ll.lat, lng: ll.lng, source: "manual" });
      });
      markerRef.current = marker;
    } else {
      markerRef.current.setLatLng([pin.lat, pin.lng]);
      markerRef.current.setIcon(icon);
    }
  }, [pin, leaflet]);

  const buscar = useCallback(async () => {
    setSearchBusy(true);
    setSearchMsg(null);
    setCandidates([]);
    try {
      const params = new URLSearchParams();
      if (addrInput.trim()) params.set("direccion", addrInput.trim());
      const r = await authFetch(`/v2/deliveries/geo/search?${params.toString()}`, { token });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j?.ok === false) {
        throw new Error(friendlyMessage(bestError(r, j as Record<string, unknown>, ""), "No se pudo buscar la dirección"));
      }
      const found: GeocodeCandidate[] = j.candidates ?? [];
      setCandidates(found);
      const best = found[0] ?? null;
      const map = mapRef.current;
      if (best) {
        setPin({ lat: best.latitude, lng: best.longitude, source: "auto", level: best.confidence.level });
        if (map) map.setView([best.latitude, best.longitude], STORE_GEO_ZOOM_SELECCION);
      } else {
        setPin(null);
        setSearchMsg("Sin resultados para esta búsqueda.");
      }
    } catch (e) {
      setSearchMsg(e instanceof Error ? e.message : "Error buscando la dirección");
    } finally {
      setSearchBusy(false);
    }
  }, [addrInput, token]);

  useEffect(() => {
    if (leaflet) buscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaflet]);

  const elegirCandidato = (c: GeocodeCandidate) => {
    setPin({ lat: c.latitude, lng: c.longitude, source: "auto", level: c.confidence.level });
    const map = mapRef.current;
    if (map) map.setView([c.latitude, c.longitude], STORE_GEO_ZOOM_SELECCION);
  };

  const confirmar = async () => {
    if (!pin) return;
    setSaving(true);
    setErr(null);
    try {
      const r = await authFetch(`/v2/stores/${storeId}/location`, {
        method: "PUT",
        token,
        json: true,
        body: JSON.stringify({ latitude: pin.lat, longitude: pin.lng }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j?.ok === false) {
        throw new Error(friendlyMessage(bestError(r, j as Record<string, unknown>, ""), "No se pudo guardar la ubicación"));
      }
      const saved = (j as { store?: { latitude?: number; longitude?: number } }).store ?? {};
      onConfirmed({
        latitude: saved.latitude ?? pin.lat,
        longitude: saved.longitude ?? pin.lng,
      });
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error guardando la ubicación");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Geolocalizar tienda" onClose={onClose} width="760px">
      <div style={{ display: "flex", gap: "10px", marginBottom: "12px", flexWrap: "wrap", alignItems: "flex-end" }}>
        <div style={{ flex: 1, minWidth: "220px" }}>
          <Input
            label="Buscar dirección"
            value={addrInput}
            onChange={(e) => setAddrInput(e.target.value)}
            placeholder="Av. Corrientes 1234, 1414, CABA"
          />
        </div>
        <Button onClick={buscar} disabled={searchBusy}>
          {searchBusy ? "Buscando..." : "Buscar"}
        </Button>
      </div>

      {searchMsg && (
        <div style={{ marginBottom: "10px", color: "var(--text-secondary)", fontSize: "13px" }}>{searchMsg}</div>
      )}

      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: "320px",
          borderRadius: "14px",
          overflow: "hidden",
          border: "1px solid var(--border)",
        }}
      />

      {candidates.length > 1 && (
        <div style={{ marginTop: "10px", display: "flex", flexDirection: "column", gap: "6px" }}>
          {candidates.map((c, i) => (
            <div
              key={i}
              onClick={() => elegirCandidato(c)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 10px",
                borderRadius: "10px",
                cursor: "pointer",
                border: "1px solid var(--border)",
              }}
            >
              <span
                style={{
                  display: "inline-block",
                  width: "9px",
                  height: "9px",
                  borderRadius: "50%",
                  background: STORE_GEO_LEVEL_COLOR[c.confidence.level],
                  flexShrink: 0,
                }}
              />
              <span style={{ fontSize: "13px" }}>
                {c.displayName ?? c.address ?? `${c.latitude}, ${c.longitude}`}
              </span>
              <span style={{ marginLeft: "auto", fontSize: "12px", color: "var(--text-secondary)" }}>
                {STORE_GEO_LEVEL_LABEL[c.confidence.level]}
              </span>
            </div>
          ))}
        </div>
      )}

      <p style={{ ...smallMutedStyle, marginTop: "10px" }}>
        Hacé click en el mapa o arrastrá el pin para ajustar la ubicación manualmente.
      </p>

      {err && <div style={{ marginTop: "10px", color: "var(--danger)", fontSize: "14px" }}>{err}</div>}

      <div style={{ marginTop: "14px", display: "flex", gap: "12px", flexWrap: "wrap" }}>
        <Button onClick={confirmar} disabled={!pin || saving}>
          {saving ? "Guardando..." : "Guardar ubicación"}
        </Button>
      </div>
    </Modal>
  );
}

function pickCreatedAccountId(payload: CreateAccountResponse): number | null {
  const raw =
    payload?.accountID ??
    payload?.accountId ??
    payload?.id ??
    payload?.data?.accountID ??
    payload?.data?.accountId ??
    payload?.data?.id ??
    payload?.result?.accountID ??
    payload?.result?.accountId ??
    payload?.result?.id ??
    null;

  if (raw === null || raw === undefined || raw === "") return null;

  const parsed = Number(raw);
  return Number.isNaN(parsed) ? null : parsed;
}

function isAlreadyLinkedResponse(resp: Response | null, body: Record<string, unknown>) {
  const raw = `${body?.error || ""} ${body?.message || ""}`.toLowerCase();

  return (
    resp?.status === 409 ||
    raw.includes("already") ||
    raw.includes("duplicate") ||
    raw.includes("duplicado") ||
    raw.includes("ya existe") ||
    raw.includes("ya vincul")
  );
}

async function linkAccountToStoreRobustly({
  token,
  storeId,
  accountId,
}: {
  token: string | null;
  storeId: number | string | null;
  accountId: number | string;
}) {
  if (!token) throw new Error("Token no disponible.");
  if (storeId === null || storeId === undefined || storeId === "") {
    throw new Error("No se pudo identificar el store para vincular la account.");
  }

  const storeID = Number(storeId);
  const accountID = Number(accountId);

  if (Number.isNaN(storeID) || Number.isNaN(accountID)) {
    throw new Error("Store ID o Account ID inválido.");
  }

  const attempts = [
    {
      endpoint: `/v2/stores/${storeID}/account`,
      body: { accountID, storeID },
    },
    {
      endpoint: `/v2/stores/${storeID}/account`,
      body: { accountId: accountID, storeId: storeID },
    },
    {
      endpoint: `/v2/accounts/${accountID}/store`,
      body: { storeID, accountID },
    },
    {
      endpoint: `/v2/accounts/${accountID}/store`,
      body: { storeId: storeID, accountId: accountID },
    },
  ];

  let lastError = "No se pudo vincular la account al store";

  for (const attempt of attempts) {
    try {
      console.log("[Stores] Vinculando account con store:", attempt);

      const resp = await authFetch(attempt.endpoint, {
        method: "POST",
        token,
        json: true,
        body: JSON.stringify(attempt.body),
      });

      const json = (await readJsonSafe(resp)) as GenericResponse;

      if ((resp.ok && json?.ok !== false) || isAlreadyLinkedResponse(resp, json as Record<string, unknown>)) {
        console.log("[Stores] Account vinculada al store correctamente:", {
          storeID,
          accountID,
          endpoint: attempt.endpoint,
          response: json,
        });

        return;
      }

      lastError = bestError(
        resp,
        json as Record<string, unknown>,
        "No se pudo vincular la account al store"
      );
    } catch (error) {
      lastError = error instanceof Error ? error.message : lastError;
    }
  }

  throw new Error(friendlyMessage(lastError, "No se pudo vincular la account al store"));
}

async function updateAccountEntryOnly(
  token: string | null,
  accountId: number,
  autoEntry: boolean
) {
  const attempts = [
    {
      method: "PUT",
      body: { auto_entry: autoEntry },
    },
    {
      method: "PUT",
      body: { entry: autoEntry },
    },
    {
      method: "PUT",
      body: { value: autoEntry },
    },
    {
      method: "PATCH",
      body: { auto_entry: autoEntry },
    },
  ] as const;

  let lastError = "No se pudo actualizar auto-entry";

  for (const attempt of attempts) {
    try {
      const resp = await authFetch(`/v2/accounts/${accountId}/entry`, {
        method: attempt.method,
        token,
        json: true,
        body: JSON.stringify(attempt.body),
      });

      const json = (await readJsonSafe(resp)) as GenericResponse;
      if (resp.ok && json?.ok !== false) return;

      lastError = bestError(
        resp,
        json as Record<string, unknown>,
        "No se pudo actualizar auto-entry"
      );
    } catch (error) {
      lastError = error instanceof Error ? error.message : lastError;
    }
  }

  throw new Error(friendlyMessage(lastError, "No se pudo actualizar auto-entry"));
}

async function updateAccountSafely(
  token: string | null,
  account: {
    id: number;
    provider: string;
    display_name: string;
    ml_user_id: number | null;
    auto_entry: boolean;
  }
) {
  const payload = {
    account: {
      provider: account.provider,
      display_name: account.display_name,
      MLUserID: account.ml_user_id,
      auto_entry: account.auto_entry,
    },
  };

  const resp = await authFetch(`/v2/accounts/${account.id}`, {
    method: "PUT",
    token,
    json: true,
    body: JSON.stringify(payload),
  });

  const json = (await readJsonSafe(resp)) as GenericResponse;

  if (!resp.ok || json?.ok === false) {
    throw new Error(
      friendlyMessage(
        bestError(resp, json as Record<string, unknown>, ""),
        "No se pudo actualizar la cuenta"
      )
    );
  }
}

/* ===== Página ===== */
export default function Stores() {
  const { token } = useAuth();

  const [stores, setStores] = useState<StoreItem[]>([]);
  const [sellers, setSellers] = useState<SellerOption[]>([]);
  const [storeTariffs, setStoreTariffs] = useState<StoreTariffOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [editStore, setEditStore] = useState<StoreItem | null>(null);
  const [viewStore, setViewStore] = useState<StoreItem | null>(null);

  // Descuento (Liquidaciones > config/stores, ver informe "LYNX — Mejoras
  // Frontend — Auditoría del ciclo semanal", Parte 1): requiere
  // finance_access, que no todo admin tiene — si el fetch falla por
  // permisos, la columna simplemente muestra "—" en vez de romper la
  // pantalla (nunca bloqueante para el resto de Tiendas).
  const [descuentoByStoreId, setDescuentoByStoreId] = useState<Record<string, number>>({});
  const [formaPagoByStoreId, setFormaPagoByStoreId] = useState<Record<string, string | null>>({});

  // Paginación/búsqueda/orden reales del listado (ver informe "LYNX —
  // Optimización definitiva de Stores y despliegue"): antes `load()` traía
  // SIEMPRE las store_data completas y el filtro de texto era 100% en
  // memoria sobre ese array ya cargado. Ahora el filtro/orden/página se
  // mandan al backend (GET /v2/stores?page=...&pageSize=...&q=...&sort=...
  // &dir=...&active=...) y `stores` pasa a contener solo la página actual.
  // El endpoint es el MISMO que usan Deliveries/Colectas/Rutas/Turbo/
  // Etiquetas/Lightdata -- a ellos no se les tocó nada: siguen llamándolo
  // sin query params y reciben el array completo de siempre.
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(1);
  const [totalStoresCount, setTotalStoresCount] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState<"all" | "true" | "false">("all");
  const [sortField, setSortField] = useState<"name" | "categoria" | "created_at" | "updated_at" | "id">("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Cualquier cambio de búsqueda/filtro/orden vuelve a la página 1 (si no,
  // se podría quedar en una página que ya no existe para el nuevo criterio).
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, activeFilter, sortField, sortDir]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const storesParams = new URLSearchParams();
      storesParams.set("page", String(page));
      storesParams.set("pageSize", String(PAGE_SIZE));
      storesParams.set("sort", sortField);
      storesParams.set("dir", sortDir);
      if (debouncedSearch) storesParams.set("q", debouncedSearch);
      if (activeFilter !== "all") storesParams.set("active", activeFilter);

      const [stRes, seRes, usRes, taRes, cfgRes] = await Promise.all([
        authFetch(`/v2/stores?${storesParams.toString()}`, { token }),
        authFetch("/v2/sellers", { token }),
        authFetch("/v2/users", { token }),
        authFetch("/v2/tariffs", { token }),
        authFetch("/v1/liquidaciones/config/stores", { token }),
      ]);

      const st: StoresResponse & { total?: number } = await stRes.json().catch(() => ({}));
      const se: SellersResponse = await seRes.json().catch(() => ({}));
      const us: UsersResponse = await usRes.json().catch(() => ({}));
      const ta: TariffsResponse = await taRes.json().catch(() => ({}));
      const cfg: {
        ok?: boolean;
        stores?: Array<{ id: number | string; descuento?: number | string; forma_pago?: string | null }>;
      } = await cfgRes.json().catch(() => ({}));

      if (!stRes.ok || st?.ok === false) {
        throw new Error(friendlyMessage(st?.error, "No se pudo cargar Tiendas"));
      }

      const normalizedStores = (pickStoresArray(st) || []).map(normalizeStore);
      setStores(normalizedStores);
      setTotalStoresCount(typeof st?.total === "number" ? st.total : normalizedStores.length);

      if (!taRes.ok || ta?.ok === false) {
        setStoreTariffs([]);
      } else {
        setStoreTariffs(
          pickTariffsArray(ta)
            .map(normalizeStoreTariff)
            .filter((item): item is StoreTariffOption => Boolean(item))
        );
      }

      if (!seRes.ok || se?.ok === false) {
        setSellers([]);
      } else {
        const sellerRows = pickSellersArray(se) || [];
        const userRows = usRes.ok && us?.ok !== false ? pickUsersArray(us) || [] : [];
        setSellers(normalizeSellerOptions(sellerRows, userRows));
      }

      if (!cfgRes.ok || cfg?.ok === false || !Array.isArray(cfg.stores)) {
        setDescuentoByStoreId({});
        setFormaPagoByStoreId({});
      } else {
        setDescuentoByStoreId(
          Object.fromEntries(cfg.stores.map((s) => [String(s.id), Number(s.descuento) || 0]))
        );
        setFormaPagoByStoreId(
          Object.fromEntries(cfg.stores.map((s) => [String(s.id), s.forma_pago ?? null]))
        );
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error cargando datos");
    } finally {
      setLoading(false);
    }
  }, [token, page, debouncedSearch, activeFilter, sortField, sortDir]);

  const toggleStoreActive = async (store: StoreItem) => {
    if (store.id == null) return;

    const confirmMsg = store.active
      ? `¿Desactivar la tienda "${store.name}"? Dejará de operar hasta reactivarla.`
      : `¿Activar la tienda "${store.name}"?`;
    if (!window.confirm(confirmMsg)) return;

    try {
      const r = await authFetch(`/v2/stores/${store.id}/activate`, {
        method: "PUT",
        token,
      });
      const j = (await readJsonSafe(r)) as GenericResponse;

      if (!r.ok || j?.ok === false) {
        throw new Error(
          friendlyMessage(
            bestError(r, j as Record<string, unknown>, ""),
            "No se pudo actualizar el estado de la tienda"
          )
        );
      }

      await load();
    } catch (e) {
      console.error("toggleStoreActive error:", e);
      alert(e instanceof Error ? e.message : "Error actualizando el estado de la tienda");
    }
  };

  const deleteStore = async (storeId: number | string | null) => {
    const ok = window.confirm(`¿Eliminar tienda ID ${storeId}?`);
    if (!ok || storeId == null) return;

    try {
      const r = await authFetch(`/v2/stores/${storeId}`, {
        method: "DELETE",
        token,
      });
      const j = (await readJsonSafe(r)) as GenericResponse;

      if (!r.ok || j?.ok === false) {
        throw new Error(
          friendlyMessage(
            bestError(r, j as Record<string, unknown>, ""),
            "No se pudo eliminar la tienda"
          )
        );
      }

      await load();
    } catch (e) {
      console.error("deleteStore error:", e);
      alert(e instanceof Error ? e.message : "Error eliminando tienda");
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  // `stores` ya es solo la página actual (ver GET /v2/stores paginado más
  // arriba): el total real de tiendas registradas es `totalStoresCount`
  // (viene del backend), no `stores.length`.
  const totalStores = totalStoresCount;
  // Se calcula sobre la página actual, no sobre el total -- paginar implica
  // que ya no hay en memoria todas las filas para contar esto a nivel global
  // sin pedirlo aparte al backend. Ver informe "LYNX — Optimización
  // definitiva de Stores y despliegue".
  const totalConCategoria = useMemo(
    () => stores.filter((s) => s.business_name && s.business_name !== "—").length,
    [stores]
  );
  const totalSellersDisponibles = sellers.length;
  const totalPages = Math.max(1, Math.ceil(totalStoresCount / PAGE_SIZE));

  return (
    <div style={pageWrapStyle}>
      <div style={headerRowStyle}>
        <div>
          <h1 style={titleStyle}>Tiendas</h1>
          <p style={subtitleStyle}>
            Gestión de tiendas, sellers vinculados y cuentas de e-commerce conectadas.
          </p>
        </div>

        <div style={actionsWrapStyle}>
          <Button variant="secondary" onClick={load}>
            Actualizar
          </Button>
          <Button onClick={() => setCreateOpen(true)}>Nueva tienda</Button>
        </div>
      </div>

      <div style={statsGridStyle} className="stores-kpi-grid">
        <Card>
          <div style={kpiLabelStyle}>Tiendas cargadas</div>
          <div style={kpiValueStyle}>{totalStores}</div>
          <div style={kpiMetaStyle}>Cantidad total de tiendas registradas.</div>
        </Card>

        <Card>
          <div style={kpiLabelStyle}>Con categoría</div>
          <div style={kpiValueStyle}>{totalConCategoria}</div>
          <div style={kpiMetaStyle}>En esta página ({stores.length} tiendas).</div>
        </Card>

        <Card>
          <div style={kpiLabelStyle}>Sellers disponibles</div>
          <div style={kpiValueStyle}>{totalSellersDisponibles}</div>
          <div style={kpiMetaStyle}>Opciones disponibles para asignación.</div>
        </Card>

        <Card>
          <div style={kpiLabelStyle}>Estado</div>
          <div style={kpiValueStyle}>{loading ? "..." : "OK"}</div>
          <div style={kpiMetaStyle}>{error ? error : "Módulo operativo."}</div>
        </Card>
      </div>

      <Card>
        <div style={{ marginBottom: "14px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px", flexWrap: "wrap" }}>
          <div>
            <h2 style={sectionTitleStyle}>Listado de tiendas</h2>
            <p style={sectionTextStyle}>
              Vista general con acceso a ver, editar y eliminar cada tienda.
            </p>
          </div>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ minWidth: "220px" }}>
              <Input
                placeholder="Buscar por nombre, categoría o dirección…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <select
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value as "all" | "true" | "false")}
              style={{ ...selectStyle, width: "auto", minHeight: "38px" }}
            >
              <option value="all">Todas</option>
              <option value="true">Activas</option>
              <option value="false">Inactivas</option>
            </select>
            <select
              value={sortField}
              onChange={(e) => setSortField(e.target.value as typeof sortField)}
              style={{ ...selectStyle, width: "auto", minHeight: "38px" }}
            >
              <option value="name">Ordenar por nombre</option>
              <option value="categoria">Ordenar por categoría</option>
              <option value="created_at">Ordenar por fecha de alta</option>
              <option value="updated_at">Ordenar por última edición</option>
              <option value="id">Ordenar por ID</option>
            </select>
            <select
              value={sortDir}
              onChange={(e) => setSortDir(e.target.value as "asc" | "desc")}
              style={{ ...selectStyle, width: "auto", minHeight: "38px" }}
            >
              <option value="asc">Ascendente</option>
              <option value="desc">Descendente</option>
            </select>
          </div>
        </div>

        {error && (
          <div
            style={{ marginBottom: "16px", color: "var(--danger)", fontSize: "14px" }}
          >
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
            Cargando…
          </div>
        ) : stores.length === 0 ? (
          <div style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
            {debouncedSearch || activeFilter !== "all"
              ? `Ninguna tienda coincide con los filtros aplicados${debouncedSearch ? ` ("${debouncedSearch}")` : ""}.`
              : "Sin tiendas aún."}
          </div>
        ) : (
          <div style={tableWrapStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Tienda</th>
                  <th style={thStyle}>Ubicación</th>
                  <th style={thStyle}>Colecta</th>
                  <th style={thStyle}>Tarifa</th>
                  <th style={thStyle}>Descuento</th>
                  <th style={thStyle}>Forma de pago</th>
                  <th style={thStyle}>Estado</th>
                  <th style={thStyle}>Acciones</th>
                </tr>
              </thead>

              <tbody>
                {stores.map((s) => {
                  return (
                    <tr key={s.id ?? `${s.slug}-${s.name}`}>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 700 }}>
                          {s.name}
                          {s.turboEnabled ? (
                            <span title="Tiene envíos Turbo" aria-label="Tiene envíos Turbo" style={{ marginLeft: "6px" }}>
                              ⚡
                            </span>
                          ) : null}
                        </div>
                        <div style={{ ...smallMutedStyle, marginTop: "2px" }}>
                          ID {s.slug}
                          {s.business_name && s.business_name !== "—"
                            ? ` · ${s.business_name}`
                            : ""}
                        </div>
                      </td>
                      <td style={{ ...tdStyle, color: "var(--text-secondary)" }}>
                        {s.address ? (
                          <div>{s.address}</div>
                        ) : (
                          <div style={{ color: "var(--text-secondary)" }}>Sin dirección</div>
                        )}
                        {s.postalCode ? (
                          <div style={smallMutedStyle}>CP {s.postalCode}</div>
                        ) : null}
                      </td>
                      <td style={{ ...tdStyle, color: "var(--text-secondary)" }}>
                        Ver en detalle
                      </td>
                      <td style={{ ...tdStyle, color: "var(--text-secondary)" }}>
                        {s.tarifaID != null ? getTariffName(storeTariffs, s.tarifaID) : "Sin tarifa"}
                      </td>
                      <td style={{ ...tdStyle, color: "var(--text-secondary)" }}>
                        {s.id != null && descuentoByStoreId[String(s.id)] !== undefined
                          ? `${Math.round(descuentoByStoreId[String(s.id)] * 1000) / 10}%`
                          : "—"}
                      </td>
                      <td style={{ ...tdStyle, color: "var(--text-secondary)" }}>
                        {s.id != null ? formaPagoLabel(formaPagoByStoreId[String(s.id)]) : "—"}
                      </td>
                      <td style={tdStyle}>
                        <StatusBadge active={s.active} />
                      </td>
                      <td style={tdStyle}>
                        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                          <IconBtn title="Ver" onClick={() => setViewStore(s)}>
                            👁
                          </IconBtn>
                          <IconBtn title="Editar" onClick={() => setEditStore(s)}>
                            ✏️
                          </IconBtn>
                          <IconBtn
                            title={s.active ? "Desactivar" : "Activar"}
                            onClick={() => toggleStoreActive(s)}
                          >
                            {s.active ? "⏻" : "▶"}
                          </IconBtn>
                          <IconBtn title="Eliminar" onClick={() => deleteStore(s.id)}>
                            🗑
                          </IconBtn>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && totalStoresCount > 0 && (
          <div
            style={{
              marginTop: "14px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <div style={smallMutedStyle}>
              Página {page} de {totalPages} · {totalStoresCount} tiendas en total
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <Button
                variant="secondary"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                ← Anterior
              </Button>
              <Button
                variant="secondary"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                Siguiente →
              </Button>
            </div>
          </div>
        )}
      </Card>

      {createOpen && (
        <CreateStoreModal
          token={token}
          storeTariffs={storeTariffs}
          onClose={() => setCreateOpen(false)}
          onCreated={load}
        />
      )}

      {viewStore && (
        <ViewStoreModal
          token={token}
          store={viewStore}
          allSellers={sellers}
          storeTariffs={storeTariffs}
          onClose={() => setViewStore(null)}
          onEdit={() => {
            const target = viewStore;
            setViewStore(null);
            setEditStore(target);
          }}
          onChanged={async () => {
            await load();
          }}
        />
      )}

      {editStore && (
        <EditStoreModal
          token={token}
          store={editStore}
          allSellers={sellers}
          storeTariffs={storeTariffs}
          onClose={() => setEditStore(null)}
          onChanged={async () => {
            await load();
          }}
        />
      )}

      <style>
        {`
          .stores-kpi-grid {
            grid-template-columns: repeat(4, minmax(0, 1fr));
          }

          @media (max-width: 1180px) {
            .stores-kpi-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }
          }

          @media (max-width: 720px) {
            .stores-kpi-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>
    </div>
  );
}

/* ================== Crear Store ================== */
const formSectionTitleStyle: React.CSSProperties = {
  margin: "0 0 4px 0",
  fontSize: "14px",
  fontWeight: 700,
  color: "var(--text-primary)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const formSectionStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "14px",
  paddingBottom: "20px",
  marginBottom: "20px",
  borderBottom: "1px solid var(--border)",
};

function CreateStoreModal({
  token,
  storeTariffs,
  onClose,
  onCreated,
}: CreateStoreModalProps) {
  const [form, setForm] = useState({
    name: "",
    business_name: "",
    address: "",
    postalCode: "",
    tarifaID: "",
  });
  const [schedule, setSchedule] = useState<GroupedWeeklySchedule>(createEmptyGroupedSchedule());
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const create = async () => {
    if (!form.name.trim()) {
      setErr("El nombre de la tienda es obligatorio.");
      return;
    }

    setSubmitting(true);
    setErr(null);
    setWarning(null);

    try {
      const payload = {
        nombre_fantasia: form.name.trim() || null,
        categoria: form.business_name.trim() || null,
        direccion: form.address.trim() || null,
        codigo_postal: form.postalCode.trim() || null,
        tarifaID: form.tarifaID ? Number(form.tarifaID) : null,
      };

      const r = await authFetch("/v2/stores", {
        method: "POST",
        token,
        json: true,
        body: JSON.stringify(payload),
      });

      const j: GenericResponse & {
        storeID?: number | string;
        id?: number | string;
        data?: { id?: number | string; storeID?: number | string };
      } = await r.json().catch(() => ({}));

      if (!r.ok || !j.ok) {
        throw new Error(
          friendlyMessage(bestError(r, j as Record<string, unknown>, ""), "No se pudo crear la tienda")
        );
      }

      const createdStoreId = j.storeID || j.id || j.data?.storeID || j.data?.id || null;

      // A partir de acá la tienda ya existe: cualquier falla siguiente se
      // comunica como advertencia, nunca como "no se pudo crear la tienda".
      const warnings: string[] = [];

      if (createdStoreId) {
        try {
          await generateExternalTokenForStore(token, createdStoreId);
        } catch {
          warnings.push("No pudimos generar el token externo automáticamente.");
        }

        const cutoffsPayload = groupedScheduleToPayload(schedule);
        if (cutoffsPayload.length) {
          try {
            await saveStoreCutoffs(token, createdStoreId, cutoffsPayload);
          } catch {
            warnings.push(
              "La tienda fue creada, pero no pudimos guardar sus horarios de colecta."
            );
          }
        }
      }

      await onCreated?.();

      if (warnings.length) {
        setWarning(warnings.join(" "));
        setDone(true);
      } else {
        onClose();
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error creando tienda");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title="Nueva tienda" onClose={onClose}>
      <div style={formSectionStyle}>
        <h4 style={formSectionTitleStyle}>Datos generales</h4>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: "16px",
          }}
          className="create-store-grid"
        >
          <Input
            label="Nombre (fantasía)"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="PENSADO POR BEBES"
            disabled={done}
          />

          <Input
            label="Categoría"
            value={form.business_name}
            onChange={(e) => setForm((f) => ({ ...f, business_name: e.target.value }))}
            placeholder="MODA"
            disabled={done}
          />
        </div>
      </div>

      <div style={formSectionStyle}>
        <h4 style={formSectionTitleStyle}>Ubicación</h4>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: "16px",
          }}
          className="create-store-grid"
        >
          <Input
            label="Dirección"
            value={form.address}
            onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
            placeholder="Av. Corrientes 1234"
            disabled={done}
          />

          <Input
            label="Código postal"
            value={form.postalCode}
            onChange={(e) => setForm((f) => ({ ...f, postalCode: e.target.value }))}
            placeholder="1414"
            disabled={done}
          />
        </div>
      </div>

      <div style={formSectionStyle}>
        <h4 style={formSectionTitleStyle}>Configuración</h4>
        <div>
          <label style={{ ...smallMutedStyle, display: "block", marginBottom: "8px" }}>
            Tarifa store
          </label>
          <select
            value={form.tarifaID}
            onChange={(e) => setForm((f) => ({ ...f, tarifaID: e.target.value }))}
            style={selectStyle}
            disabled={done}
          >
            <option value="">Sin tarifa asociada</option>
            {storeTariffs.map((tariff) => (
              <option key={tariff.id} value={String(tariff.id)}>
                {formatTariffOptionLabel(tariff)}
              </option>
            ))}
          </select>
          <div style={{ ...smallMutedStyle, marginTop: "8px" }}>
            Solo se muestran tarifas de tipo STORE. Esta tarifa será usada por integraciones externas.
          </div>
        </div>
      </div>

      <div style={{ ...formSectionStyle, borderBottom: "none", marginBottom: 0 }}>
        <h4 style={formSectionTitleStyle}>Horarios de colecta</h4>
        <p style={{ ...smallMutedStyle, margin: 0 }}>
          Opcional. Lunes a viernes comparten siempre el mismo horario; sábado y domingo se
          configuran aparte. Un grupo sin horario no se colecta esos días. Podés completarlo
          ahora o más tarde desde la ficha de la tienda.
        </p>
        <StoreScheduleEditor value={schedule} onChange={setSchedule} disabled={done} />
      </div>

      {!done && (
        <div style={{ marginTop: "20px", display: "flex", gap: "12px", flexWrap: "wrap" }}>
          <Button onClick={create} disabled={submitting || !form.name.trim()}>
            {submitting ? "Creando..." : "Crear tienda"}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
        </div>
      )}

      {warning && (
        <div
          style={{
            marginTop: "16px",
            padding: "12px 14px",
            borderRadius: "14px",
            border: "1px solid rgba(200,144,52,0.3)",
            background: "rgba(200,144,52,0.12)",
            color: "var(--text-primary)",
            fontSize: "14px",
          }}
        >
          {warning}
        </div>
      )}

      {done && (
        <div style={{ marginTop: "16px", textAlign: "right" }}>
          <Button onClick={onClose}>Cerrar</Button>
        </div>
      )}

      {err && (
        <div style={{ marginTop: "12px", color: "var(--danger)", fontSize: "14px" }}>
          {err}
        </div>
      )}

      <style>
        {`
          @media (max-width: 720px) {
            .create-store-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>
    </Modal>
  );
}

/* ================== Ver Store (ficha operativa) ================== */
const fichaSectionStyle: React.CSSProperties = {
  paddingBottom: "20px",
  marginBottom: "20px",
  borderBottom: "1px solid var(--border)",
};

function ViewStoreModal({
  token,
  store,
  allSellers,
  storeTariffs,
  onClose,
  onEdit,
  onChanged,
}: ViewStoreModalProps) {
  const [loading, setLoading] = useState(true);
  const [linkedSellers, setLinkedSellers] = useState<LinkedSeller[]>([]);
  const [accounts, setAccounts] = useState<AccountItem[]>([]);
  const [schedule, setSchedule] = useState<GroupedWeeklySchedule>(createEmptyGroupedSchedule());
  const [scheduleInconsistent, setScheduleInconsistent] = useState(false);
  const [externalToken, setExternalToken] = useState(store.externalToken || "");
  const [toggling, setToggling] = useState(false);
  const [active, setActive] = useState(store.active);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);

      const [sellersResult, accountsResult, cutoffsResult, tokenResult] =
        await Promise.allSettled([
          (async () => {
            const tryEndpoints = [
              `/v2/stores/${store.id}/seller`,
              `/v2/sellers/by-store/${store.id}`,
            ];
            for (const endpoint of tryEndpoints) {
              const r = await authFetch(endpoint, { token });
              const j: LinkedSellersResponse = await r.json().catch(() => ({}));
              if (r.ok && j?.ok !== false) {
                const arr = pickLinkedSellersArray(j);
                if (arr.length) return enrichLinkedSellers(arr, allSellers);
              }
            }
            return [] as LinkedSeller[];
          })(),
          (async () => {
            const r = await authFetch(`/v2/stores/${store.id}/account`, { token });
            const j: AccountsResponse = await r.json().catch(() => ({}));
            if (!r.ok || j?.ok === false) return [] as AccountItem[];

            const baseAccounts = pickAccountsArray(j) || [];
            return Promise.all(
              baseAccounts.map(async (account) => {
                try {
                  const tr = await authFetch(`/v2/tokens/${account.id}`, { token });
                  const tj: TokensResponse = await tr.json().catch(() => ({}));
                  if (!tr.ok || tj?.ok === false) {
                    return { ...account, __hasOAuthToken: false, __tokenMlUserId: null, __resolvedProvider: account.provider || "" };
                  }
                  const tokenRecord = pickTokenRecord(tj);
                  const tokenMlUserId =
                    tokenRecord?.user_id != null && tokenRecord.user_id !== ""
                      ? Number(tokenRecord.user_id)
                      : null;
                  return {
                    ...account,
                    __hasOAuthToken: hasValidMlToken(tokenRecord),
                    __tokenMlUserId: tokenMlUserId,
                    __resolvedProvider:
                      account.provider || (isTokenMeliCandidate(tokenRecord) ? "Mercadolibre" : ""),
                  };
                } catch {
                  return { ...account, __hasOAuthToken: false, __tokenMlUserId: null, __resolvedProvider: account.provider || "" };
                }
              })
            );
          })(),
          store.id != null ? fetchStoreCutoffs(token, store.id) : Promise.resolve([]),
          store.id != null ? getExternalTokenForStore(token, store.id) : Promise.resolve(""),
        ]);

      if (cancelled) return;

      setLinkedSellers(sellersResult.status === "fulfilled" ? sellersResult.value : []);
      setAccounts(accountsResult.status === "fulfilled" ? accountsResult.value : []);
      if (cutoffsResult.status === "fulfilled") {
        const grouped = cutoffsToGroupedSchedule(cutoffsResult.value);
        setSchedule(grouped.schedule);
        setScheduleInconsistent(grouped.weekdaysInconsistent);
      } else {
        setSchedule(createEmptyGroupedSchedule());
        setScheduleInconsistent(false);
      }
      if (tokenResult.status === "fulfilled" && tokenResult.value) {
        setExternalToken(tokenResult.value);
      }

      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [store.id, token, allSellers]);

  const geoStatus = getGeoStatus(store);

  const toggleActive = async () => {
    if (store.id == null) return;
    const confirmMsg = active
      ? `¿Desactivar la tienda "${store.name}"?`
      : `¿Activar la tienda "${store.name}"?`;
    if (!window.confirm(confirmMsg)) return;

    setToggling(true);
    try {
      const r = await authFetch(`/v2/stores/${store.id}/activate`, { method: "PUT", token });
      const j = (await readJsonSafe(r)) as GenericResponse;
      if (!r.ok || j?.ok === false) {
        throw new Error(
          friendlyMessage(bestError(r, j as Record<string, unknown>, ""), "No se pudo actualizar el estado")
        );
      }
      setActive((prev) => !prev);
      await onChanged?.();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Error actualizando el estado de la tienda");
    } finally {
      setToggling(false);
    }
  };

  return (
    <Modal title={`Ficha de tienda: ${store.name}`} onClose={onClose} width="960px">
      <div style={fichaSectionStyle}>
        <h4 style={formSectionTitleStyle}>Identidad</h4>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: "16px",
            marginTop: "10px",
          }}
          className="view-store-grid"
        >
          <div style={providerCardStyle}>
            <div style={smallMutedStyle}>Nombre</div>
            <div style={{ marginTop: "6px", fontWeight: 700 }}>{store.name}</div>
          </div>

          <div style={providerCardStyle}>
            <div style={smallMutedStyle}>Categoría</div>
            <div style={{ marginTop: "6px", color: "var(--text-secondary)" }}>
              {store.business_name || "—"}
            </div>
          </div>

          <div style={providerCardStyle}>
            <div style={smallMutedStyle}>ID</div>
            <div style={{ marginTop: "6px", fontFamily: "monospace" }}>{store.id}</div>
          </div>

          <div style={{ ...providerCardStyle, gridColumn: "span 2" }}>
            <div style={smallMutedStyle}>Estado</div>
            <div style={{ marginTop: "8px" }}>
              <StatusBadge active={active} />
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "flex-end" }}>
            <Button variant="secondary" onClick={toggleActive} disabled={toggling} fullWidth>
              {toggling ? "Actualizando..." : active ? "Desactivar" : "Activar"}
            </Button>
          </div>

          <div style={{ ...providerCardStyle, gridColumn: "1 / -1" }}>
            <div style={smallMutedStyle}>Envíos Turbo</div>
            <div style={{ marginTop: "6px", color: store.turboEnabled ? "var(--text-primary)" : "var(--text-secondary)" }}>
              {store.turboEnabled ? "⚡ Sí · se muestra en Turbo · En vivo" : "No · no se muestra en Turbo · En vivo"}
            </div>
          </div>
        </div>
      </div>

      <div style={fichaSectionStyle}>
        <h4 style={formSectionTitleStyle}>Ubicación</h4>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: "16px",
            marginTop: "10px",
          }}
          className="view-store-grid"
        >
          <div style={providerCardStyle}>
            <div style={smallMutedStyle}>Dirección</div>
            <div style={{ marginTop: "6px" }}>{store.address || "Sin dirección"}</div>
          </div>

          <div style={providerCardStyle}>
            <div style={smallMutedStyle}>Código postal</div>
            <div style={{ marginTop: "6px" }}>{store.postalCode || "—"}</div>
          </div>

          <div style={{ ...providerCardStyle, gridColumn: "1 / -1" }}>
            <div style={smallMutedStyle}>Geolocalización</div>
            <div
              style={{
                marginTop: "6px",
                color: geoStatus.confirmed ? "var(--success)" : "var(--text-secondary)",
                fontWeight: geoStatus.confirmed ? 700 : 400,
              }}
            >
              {geoStatus.label}
            </div>
          </div>
        </div>
      </div>

      <div style={fichaSectionStyle}>
        <h4 style={formSectionTitleStyle}>Horarios de colecta</h4>
        <div style={{ marginTop: "10px" }}>
          {loading ? (
            <div style={{ color: "var(--text-secondary)", fontSize: "14px" }}>Cargando…</div>
          ) : (
            <>
              {scheduleInconsistent && (
                <div
                  style={{
                    marginBottom: "10px",
                    padding: "10px 12px",
                    borderRadius: "12px",
                    border: "1px solid rgba(200,144,52,0.3)",
                    background: "rgba(200,144,52,0.12)",
                    color: "var(--text-primary)",
                    fontSize: "13px",
                  }}
                >
                  Lunes a viernes tienen horarios distintos entre sí (dato previo a esta
                  regla). Se muestra el horario de lunes — revisalo en "Editar tienda".
                </div>
              )}
              <StoreScheduleSummary schedule={schedule} />
            </>
          )}
        </div>
      </div>

      <div style={fichaSectionStyle}>
        <h4 style={formSectionTitleStyle}>Tarifa</h4>
        <div style={{ ...providerCardStyle, marginTop: "10px" }}>
          {getTariffName(storeTariffs, store.tarifaID)}
        </div>
      </div>

      <div style={fichaSectionStyle}>
        <h4 style={formSectionTitleStyle}>Sellers</h4>
        <div style={{ marginTop: "10px" }}>
          {loading ? (
            <div style={{ color: "var(--text-secondary)", fontSize: "14px" }}>Cargando…</div>
          ) : linkedSellers.length === 0 ? (
            <div style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
              Sin sellers vinculados.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {linkedSellers.map((s) => (
                <div key={s.id} style={providerCardStyle}>
                  <div style={{ fontWeight: 600 }}>
                    {s.fullName || s.username || s.razon_social || `Seller ${s.id}`}
                  </div>
                  <div style={{ ...smallMutedStyle, marginTop: "4px" }}>
                    Seller ID: {s.id}
                    {s.username ? ` · @${s.username}` : ""}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={fichaSectionStyle}>
        <h4 style={formSectionTitleStyle}>Cuentas / Integraciones</h4>
        <div style={{ marginTop: "10px" }}>
          {loading ? (
            <div style={{ color: "var(--text-secondary)", fontSize: "14px" }}>Cargando…</div>
          ) : accounts.length === 0 ? (
            <div style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
              Sin cuentas vinculadas.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {accounts.map((a) => {
                const resolvedProvider = getResolvedProvider(a);
                return (
                  <div
                    key={a.id}
                    style={{
                      ...providerCardStyle,
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <AccountLogo provider={resolvedProvider} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600 }}>{getAccDisplayName(a)}</div>
                      <div style={{ ...smallMutedStyle, marginTop: "4px" }}>
                        {isMeliLinked(a) ? "✓ Vinculado" : "Pendiente de vinculación"}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div style={{ ...fichaSectionStyle, borderBottom: "none", marginBottom: 0 }}>
        <h4 style={formSectionTitleStyle}>Integración externa</h4>
        <div
          style={{
            marginTop: "10px",
            color: externalToken ? "var(--text-primary)" : "var(--text-secondary)",
            fontFamily: externalToken ? "monospace" : undefined,
            fontSize: externalToken ? "12px" : "14px",
            wordBreak: "break-all",
          }}
        >
          {externalToken || "Sin token externo"}
        </div>
      </div>

      <div
        style={{
          marginTop: "20px",
          display: "flex",
          justifyContent: "flex-end",
          gap: "12px",
          flexWrap: "wrap",
        }}
      >
        <Button variant="ghost" onClick={onClose}>
          Cerrar
        </Button>
        <Button onClick={onEdit}>Editar tienda</Button>
      </div>

      <style>
        {`
          @media (max-width: 720px) {
            .view-store-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>
    </Modal>
  );
}

/* ================== Editar Store ================== */
export function EditStoreModal({
  token,
  store,
  allSellers,
  storeTariffs,
  onClose,
  onChanged,
}: EditStoreModalProps) {
  const [tab, setTab] = useState("GENERAL");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [generalForm, setGeneralForm] = useState({
    name: store.name === "—" ? "" : store.name,
    business_name: store.business_name === "—" ? "" : store.business_name,
    tarifaID: store.tarifaID != null ? String(store.tarifaID) : "",
  });
  // Descuento (%) vive en legacy_store_financial_config, no en store_data —
  // se carga y guarda aparte, vía el endpoint de Liquidaciones que ya existe
  // (ver informe "Sprint Final — Cierre de Liquidaciones", Parte 1). Se
  // guarda en fracción (0..1) en backend, se edita en % acá.
  const [descuentoPct, setDescuentoPct] = useState<string>("0");
  const [formaPago, setFormaPago] = useState<string>("");
  const [locationForm, setLocationForm] = useState({
    address: store.address || "",
    postalCode: store.postalCode || "",
  });
  // Estado de geolocalización propio del modal: arranca desde la Store tal
  // como llegó, pero se actualiza localmente al confirmar un punto nuevo o
  // al guardar un cambio de Dirección/CP (que invalida el punto anterior en
  // el backend) — así el indicador ✅/⚠ queda correcto sin cerrar y volver a
  // abrir el modal. `persistedAddress` es la Dirección/CP tal como quedaron
  // guardados en la Store la última vez (no lo que el admin esté tipeando
  // sin guardar todavía): el picker de geolocalización siempre busca sobre
  // ESO, nunca sobre un cambio de dirección aún no guardado.
  const [geoCoords, setGeoCoords] = useState<{ latitude: number | null; longitude: number | null }>({
    latitude: store.latitude,
    longitude: store.longitude,
  });
  const [persistedAddress, setPersistedAddress] = useState({
    address: store.address || "",
    postalCode: store.postalCode || "",
  });
  const [showGeoModal, setShowGeoModal] = useState(false);
  const [externalToken, setExternalToken] = useState<string>(store.externalToken || "");
  const [active, setActive] = useState<boolean>(store.active);
  const [togglingActive, setTogglingActive] = useState(false);
  const [turboEnabled, setTurboEnabled] = useState<boolean>(store.turboEnabled);
  const [savingTurbo, setSavingTurbo] = useState(false);

  const [schedule, setSchedule] = useState<GroupedWeeklySchedule>(createEmptyGroupedSchedule());
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [scheduleErr, setScheduleErr] = useState<string | null>(null);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleSaved, setScheduleSaved] = useState(false);
  const [scheduleInconsistent, setScheduleInconsistent] = useState(false);

  const [sellerSearch, setSellerSearch] = useState("");
  const [addingSellerId, setAddingSellerId] = useState("");
  const [linkedSellers, setLinkedSellers] = useState<LinkedSeller[]>([]);

  // Tab Legacy (ver informe "LYNX — Mejoras Frontend — Auditoría del ciclo
  // semanal", Parte 1): alias de legacy_store_map, nunca mezclado con nada
  // de Current/Mercado Libre/Sellers.
  const [legacyAliases, setLegacyAliases] = useState<{ id: number; legacy_nombre_fantasia: string; mapped_at: string }[]>([]);
  const [legacyLoading, setLegacyLoading] = useState(true);
  const [legacyErr, setLegacyErr] = useState<string | null>(null);
  const [newAliasValue, setNewAliasValue] = useState("");
  const [addingAlias, setAddingAlias] = useState(false);
  const [removingAliasId, setRemovingAliasId] = useState<number | null>(null);

  const [accounts, setAccounts] = useState<AccountItem[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<AccountItem | null>(null);

  const [addingAcc, setAddingAcc] = useState<{
    provider: ProviderValue;
    accountIdentifier: string;
    displayName: string;
  }>({
    provider: "mercadolibre",
    accountIdentifier: "",
    displayName: "",
  });

  const loadLinkedSellers = async () => {
    try {
      const tryEndpoints = [
        `/v2/stores/${store.id}/seller`,
        `/v2/sellers/by-store/${store.id}`,
      ];

      for (const endpoint of tryEndpoints) {
        const r = await authFetch(endpoint, { token });
        const j: LinkedSellersResponse = await r.json().catch(() => ({}));
        if (r.ok && j?.ok !== false) {
          const arr = pickLinkedSellersArray(j);
          if (arr.length) {
            setLinkedSellers(enrichLinkedSellers(arr, allSellers));
            return;
          }
        }
      }

      setLinkedSellers([]);
    } catch {
      setLinkedSellers([]);
    }
  };

  const loadLegacyAliases = async () => {
    if (store.id == null) {
      setLegacyLoading(false);
      return;
    }
    setLegacyLoading(true);
    setLegacyErr(null);
    try {
      const r = await authFetch(`/v1/liquidaciones/config/stores/${store.id}/aliases`, { token });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j?.ok === false) {
        throw new Error(friendlyMessage(j?.error, "No se pudieron cargar los alias Legacy"));
      }
      setLegacyAliases(j.aliases || []);
    } catch (e) {
      setLegacyErr(e instanceof Error ? e.message : "Error cargando alias Legacy");
      setLegacyAliases([]);
    } finally {
      setLegacyLoading(false);
    }
  };

  const addLegacyAlias = async () => {
    const value = newAliasValue.trim();
    if (!value || store.id == null) return;
    setAddingAlias(true);
    setLegacyErr(null);
    try {
      const r = await authFetch("/v1/liquidaciones/config/store-map", {
        method: "POST",
        token,
        json: true,
        body: JSON.stringify({ legacyNombreFantasia: value, storeId: Number(store.id) }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j?.ok === false) {
        throw new Error(friendlyMessage(j?.error, "No se pudo agregar el alias"));
      }
      setNewAliasValue("");
      await loadLegacyAliases();
    } catch (e) {
      setLegacyErr(e instanceof Error ? e.message : "Error agregando el alias");
    } finally {
      setAddingAlias(false);
    }
  };

  const removeLegacyAlias = async (aliasId: number, aliasName: string) => {
    if (!window.confirm(`¿Eliminar el alias Legacy "${aliasName}"? Los envíos con ese cadete van a dejar de resolverse a este Store hasta que se vuelva a mapear.`)) return;
    setRemovingAliasId(aliasId);
    setLegacyErr(null);
    try {
      const r = await authFetch(`/v1/liquidaciones/config/store-map/${aliasId}`, { method: "DELETE", token });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j?.ok === false) {
        throw new Error(friendlyMessage(j?.error, "No se pudo eliminar el alias"));
      }
      await loadLegacyAliases();
    } catch (e) {
      setLegacyErr(e instanceof Error ? e.message : "Error eliminando el alias");
    } finally {
      setRemovingAliasId(null);
    }
  };

  const decorateAccountsWithOAuthStatus = async (
    rawAccounts: AccountItem[]
  ): Promise<AccountItem[]> => {
    return Promise.all(
      (rawAccounts || []).map(async (account) => {
        try {
          const r = await authFetch(`/v2/tokens/${account.id}`, { token });
          const j: TokensResponse = await r.json().catch(() => ({}));

          if (!r.ok || j?.ok === false) {
            return {
              ...account,
              __hasOAuthToken: false,
              __tokenMlUserId: null,
              __resolvedProvider: account.provider || "",
            };
          }

          const tokenRecord = pickTokenRecord(j);
          const tokenMlUserId =
            tokenRecord?.user_id != null && tokenRecord.user_id !== ""
              ? Number(tokenRecord.user_id)
              : null;

          const hasMlToken = hasValidMlToken(tokenRecord);
          const resolvedProvider =
            account.provider ||
            (isTokenMeliCandidate(tokenRecord) ? "Mercadolibre" : "");

          return {
            ...account,
            __hasOAuthToken: hasMlToken,
            __tokenMlUserId: tokenMlUserId,
            __resolvedProvider: resolvedProvider,
          };
        } catch {
          return {
            ...account,
            __hasOAuthToken: false,
            __tokenMlUserId: null,
            __resolvedProvider: account.provider || "",
          };
        }
      })
    );
  };

  const loadAccounts = async () => {
    try {
      const r = await authFetch(`/v2/stores/${store.id}/account`, { token });
      const j: AccountsResponse = await r.json().catch(() => ({}));
      if (!r.ok || j?.ok === false) {
        throw new Error(j?.error || "No se pudieron cargar cuentas");
      }

      const baseAccounts = pickAccountsArray(j) || [];
      const withStatus = await decorateAccountsWithOAuthStatus(baseAccounts);
      setAccounts(withStatus);

      setSelectedAccount((prev) => {
        if (!prev) return prev;
        const updatedAccount = withStatus.find(
          (item) => String(item.id) === String(prev.id)
        );
        return updatedAccount || prev;
      });
    } catch {
      setAccounts([]);
    }
  };

  const loadExternalToken = async () => {
    const tokenValue = await getExternalTokenForStore(token, store.id);
    if (tokenValue) {
      setExternalToken(tokenValue);
    }
  };

  const loadDescuento = async () => {
    if (store.id == null) return;
    try {
      const r = await authFetch("/v1/liquidaciones/config/stores", { token });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j?.ok === false || !Array.isArray(j.stores)) return;
      const row = j.stores.find((s: { id: number | string }) => String(s.id) === String(store.id));
      const frac = row?.descuento != null ? Number(row.descuento) : 0;
      setDescuentoPct(String(Math.round(frac * 1000) / 10));
      setFormaPago(row?.forma_pago ?? "");
    } catch {
      // Silencioso: si no carga, se asume 0% — el usuario ve el campo en 0
      // y puede escribir el valor real, nunca se bloquea la edición.
    }
  };

  const loadSchedule = async () => {
    if (store.id == null) {
      setScheduleLoading(false);
      return;
    }
    setScheduleLoading(true);
    setScheduleErr(null);
    try {
      const cutoffs = await fetchStoreCutoffs(token, store.id);
      const grouped = cutoffsToGroupedSchedule(cutoffs);
      setSchedule(grouped.schedule);
      setScheduleInconsistent(grouped.weekdaysInconsistent);
    } catch (e) {
      setScheduleErr(
        friendlyMessage(
          e instanceof Error ? e.message : null,
          "No se pudieron cargar los horarios de colecta"
        )
      );
    } finally {
      setScheduleLoading(false);
    }
  };

  useEffect(() => {
    loadAccounts();
    loadLinkedSellers();
    loadExternalToken();
    loadSchedule();
    loadDescuento();
    loadLegacyAliases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.id]);

  const saveSchedule = async () => {
    if (store.id == null) return;
    setScheduleSaving(true);
    setScheduleErr(null);
    setScheduleSaved(false);
    try {
      const payload = groupedScheduleToPayload(schedule);
      const cutoffs = await saveStoreCutoffs(token, store.id, payload);
      const grouped = cutoffsToGroupedSchedule(cutoffs);
      setSchedule(grouped.schedule);
      setScheduleInconsistent(grouped.weekdaysInconsistent);
      setScheduleSaved(true);
      await onChanged?.();
    } catch (e) {
      setScheduleErr(
        friendlyMessage(
          e instanceof Error ? e.message : null,
          "No se pudieron guardar los horarios de colecta"
        )
      );
    } finally {
      setScheduleSaving(false);
    }
  };

  const toggleActive = async () => {
    if (store.id == null) return;
    const confirmMsg = active
      ? `¿Desactivar la tienda "${generalForm.name || store.name}"?`
      : `¿Activar la tienda "${generalForm.name || store.name}"?`;
    if (!window.confirm(confirmMsg)) return;

    setTogglingActive(true);
    setErr(null);
    try {
      const r = await authFetch(`/v2/stores/${store.id}/activate`, { method: "PUT", token });
      const j = (await readJsonSafe(r)) as GenericResponse;
      if (!r.ok || j?.ok === false) {
        throw new Error(
          friendlyMessage(bestError(r, j as Record<string, unknown>, ""), "No se pudo actualizar el estado")
        );
      }
      setActive((prev) => !prev);
      await onChanged?.();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error actualizando el estado de la tienda");
    } finally {
      setTogglingActive(false);
    }
  };

  // Envíos Turbo: se guarda al tildar, aparte de «Guardar datos», igual que la
  // habilitación para Turbo del chofer. Solo decide si la tienda se dibuja en
  // Turbo · En vivo.
  const saveTurboEnabled = async (enabled: boolean) => {
    if (store.id == null) return;
    setSavingTurbo(true);
    setErr(null);
    try {
      const r = await authFetch(`/v2/stores/${store.id}/turbo-enabled`, {
        method: "PUT",
        token,
        json: true,
        body: JSON.stringify({ turbo_enabled: enabled }),
      });
      const j = (await readJsonSafe(r)) as GenericResponse;
      if (!r.ok || j?.ok === false) {
        throw new Error(
          friendlyMessage(bestError(r, j as Record<string, unknown>, ""), "No se pudo cambiar la marca de envíos Turbo")
        );
      }
      setTurboEnabled(enabled);
      await onChanged?.();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error cambiando la marca de envíos Turbo");
    } finally {
      setSavingTurbo(false);
    }
  };

  const filteredSellers = useMemo(() => {
    const q = sellerSearch.trim().toLowerCase();
    if (!q) return allSellers || [];
    return (allSellers || []).filter((s) =>
      [
        s.label,
        s.fullName,
        s.username,
        s.email,
        s.phone,
        s.razonSocial,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q))
    );
  }, [allSellers, sellerSearch]);

  const addSeller = async () => {
    if (!addingSellerId) return;
    setBusy(true);
    setErr(null);

    try {
      const r = await authFetch(`/v2/stores/${store.id}/seller`, {
        method: "POST",
        token,
        json: true,
        body: JSON.stringify({
          sellerID: Number(addingSellerId),
        }),
      });

      const j: GenericResponse = await r.json().catch(() => ({}));
      if (!r.ok || j?.ok === false) {
        throw new Error(friendlyMessage(j?.error, "No se pudo vincular seller"));
      }

      setAddingSellerId("");
      setSellerSearch("");
      await onChanged?.();
      await loadLinkedSellers();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error vinculando seller");
    } finally {
      setBusy(false);
    }
  };

const createAndLinkAccount = async () => {
  setBusy(true);
  setErr(null);

  try {
    if (store.id === null || store.id === undefined || store.id === "") {
      throw new Error("No se pudo identificar el store actual.");
    }

    const provider = normProvider(
      addingAcc.provider || "mercadolibre"
    ) as ProviderValue;

    const displayName = (addingAcc.displayName || "").trim() || "Pendiente";
    const accountIdentifier = (addingAcc.accountIdentifier || "").trim();

    const createPayload = {
      provider,
      display_name: displayName,
      account_identifier: accountIdentifier || null,
      MLUserID: provider === "mercadolibre" ? null : null,
      auto_entry: false,
    };

    console.log("[Stores] Creando account desde store:", {
      storeId: store.id,
      payload: createPayload,
    });

    const r1 = await authFetch(`/v2/accounts`, {
      method: "POST",
      token,
      json: true,
      body: JSON.stringify(createPayload),
    });

    const j1: CreateAccountResponse = await r1.json().catch(() => ({}));

    if (!r1.ok || j1?.ok === false) {
      throw new Error(friendlyMessage(j1?.error || j1?.message, "No se pudo crear la account"));
    }

    const accountID = pickCreatedAccountId(j1);

    if (!accountID) {
      throw new Error("La account se creó, pero el backend no devolvió accountID.");
    }

    await linkAccountToStoreRobustly({
      token,
      storeId: store.id,
      accountId: accountID,
    });

    // Antes: await onChanged?.() además de loadAccounts() -- eso era lo que
    // recargaba las 267 Stores completas (y, antes de esta sesión, el N+1
    // de horarios) por crear una sola Account. Alcance de esta optimización:
    // SOLO esta línea se quita; el resto de la función es idéntico al
    // original (ver informe "LYNX — ajuste de alcance").
    await loadAccounts();

    setAddingAcc({
      provider: "mercadolibre",
      accountIdentifier: "",
      displayName: "",
    });
  } catch (e) {
    console.error("[Stores] createAndLinkAccount error:", e);
    setErr(e instanceof Error ? e.message : "Error agregando cuenta");
  } finally {
    setBusy(false);
  }
};

  const deleteAccount = async (account: AccountItem) => {
    const ok = window.confirm(
      `¿Eliminar la cuenta ID ${account?.id}?${
        isMeli(getResolvedProvider(account)) ? " (MercadoLibre)" : ""
      }\n\nEsto puede fallar si el backend no permite borrar por FK.`
    );
    if (!ok) return;

    setBusy(true);
    setErr(null);

    const accountID = Number(account?.id);
    const storeID = Number(store?.id);

    try {
      try {
        const rUnlink = await authFetch(`/v2/stores/${storeID}/account`, {
          method: "DELETE",
          token,
          json: true,
          body: JSON.stringify({ accountID, storeID }),
        });

        if (!rUnlink.ok && ![404, 405].includes(rUnlink.status)) {
          const jUnlink = await readJsonSafe(rUnlink);
          console.warn("unlink account warning:", jUnlink);
        }
      } catch (e) {
        console.warn("unlink account fetch failed (ignored):", e);
      }

      const rDel = await authFetch(`/v2/accounts/${accountID}`, {
        method: "DELETE",
        token,
      });
      const jDel = (await readJsonSafe(rDel)) as GenericResponse;

      if (!rDel.ok || jDel?.ok === false) {
        throw new Error(
          friendlyMessage(
            bestError(rDel, jDel as Record<string, unknown>, ""),
            "No se pudo eliminar la account"
          )
        );
      }

      if (selectedAccount?.id === accountID) setSelectedAccount(null);
      await loadAccounts();
      await onChanged?.();
    } catch (e) {
      console.error("deleteAccount error:", e);
      setErr(e instanceof Error ? e.message : "Error eliminando account");
    } finally {
      setBusy(false);
    }
  };

  const toggleEntry = async (account: AccountItem) => {
    const nextValue = !getAutoEntry(account);
    setBusy(true);
    setErr(null);

    try {
      await updateAccountEntryOnly(token, Number(account.id), nextValue);
      await loadAccounts();
      await onChanged?.();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error actualizando auto-entry");
    } finally {
      setBusy(false);
    }
  };

  const saveGeneral = async () => {
    if (!token || store.id == null) return;

    setBusy(true);
    setErr(null);

    try {
      const descuentoValue = descuentoPct.trim() === "" ? 0 : Number(descuentoPct);
      if (!Number.isFinite(descuentoValue) || descuentoValue < 0 || descuentoValue > 100) {
        throw new Error("El descuento debe ser un porcentaje entre 0 y 100.");
      }

      const payload = {
        store: {
          tarifaID: generalForm.tarifaID ? Number(generalForm.tarifaID) : null,
          nombre_fantasia: generalForm.name.trim() || null,
          categoria: generalForm.business_name.trim() || null,
          direccion: locationForm.address.trim() || null,
          codigo_postal: locationForm.postalCode.trim() || null,
        },
      };

      const response = await authFetch(`/v2/stores/${store.id}`, {
        method: "PUT",
        token,
        json: true,
        body: JSON.stringify(payload),
      });

      const json: GenericResponse = await response.json().catch(() => ({}));

      if (!response.ok || json?.ok === false) {
        throw new Error(
          friendlyMessage(
            bestError(response, json as Record<string, unknown>, ""),
            "No se pudo actualizar la tienda"
          )
        );
      }

      // Si Dirección o Código Postal cambiaron respecto a lo último
      // guardado, la geolocalización anterior deja de corresponder a la
      // nueva dirección — el backend ya la invalida en la misma UPDATE
      // (ver PUT /v2/stores/:id), acá solo se refleja en el estado local
      // para que el indicador ✅/⚠ quede correcto sin recargar el modal.
      const newAddress = locationForm.address.trim();
      const newPostalCode = locationForm.postalCode.trim();
      const addressChanged =
        newAddress !== persistedAddress.address.trim() || newPostalCode !== persistedAddress.postalCode.trim();
      if (addressChanged) {
        setGeoCoords({ latitude: null, longitude: null });
      }
      setPersistedAddress({ address: newAddress, postalCode: newPostalCode });

      // Descuento (ver informe "Sprint Final — Cierre de Liquidaciones",
      // Parte 1): mismo endpoint que ya existía en backend
      // (PUT /v1/liquidaciones/config/stores/:storeId/descuento), nunca uno
      // nuevo. Se manda en fracción (0..1); acá se edita en %.
      const descuentoResponse = await authFetch(`/v1/liquidaciones/config/stores/${store.id}/descuento`, {
        method: "PUT",
        token,
        json: true,
        body: JSON.stringify({ descuento: descuentoValue / 100 }),
      });
      const descuentoJson: GenericResponse = await descuentoResponse.json().catch(() => ({}));
      if (!descuentoResponse.ok || descuentoJson?.ok === false) {
        throw new Error(
          friendlyMessage(
            bestError(descuentoResponse, descuentoJson as Record<string, unknown>, ""),
            "No se pudo actualizar el descuento"
          )
        );
      }

      // Forma de pago: mismo patrón y misma tabla que descuento, endpoint
      // hermano (PUT /v1/liquidaciones/config/stores/:storeId/forma-pago).
      const formaPagoResponse = await authFetch(`/v1/liquidaciones/config/stores/${store.id}/forma-pago`, {
        method: "PUT",
        token,
        json: true,
        body: JSON.stringify({ formaPago: formaPago || null }),
      });
      const formaPagoJson: GenericResponse = await formaPagoResponse.json().catch(() => ({}));
      if (!formaPagoResponse.ok || formaPagoJson?.ok === false) {
        throw new Error(
          friendlyMessage(
            bestError(formaPagoResponse, formaPagoJson as Record<string, unknown>, ""),
            "No se pudo actualizar la forma de pago"
          )
        );
      }

      await onChanged?.();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error actualizando tienda");
    } finally {
      setBusy(false);
    }
  };

  const copyExternalToken = async () => {
    if (!externalToken) return;
    try {
      await navigator.clipboard?.writeText(externalToken);
      alert("Token externo copiado.");
    } catch {
      alert("No se pudo copiar automáticamente. Copialo manualmente.");
    }
  };

  return (
    <Modal title={`Editar tienda: ${store.name}`} onClose={onClose} width="1280px">
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: "16px",
          marginBottom: "20px",
        }}
        className="edit-store-head-grid"
      >
        <div style={providerCardStyle}>
          <div style={smallMutedStyle}>Código (ID)</div>
          <div style={{ marginTop: "6px", fontFamily: "monospace" }}>{store.id}</div>
        </div>

        <div style={providerCardStyle}>
          <div style={smallMutedStyle}>Nombre</div>
          <div style={{ marginTop: "6px" }}>{generalForm.name || store.name}</div>
        </div>

        <div style={providerCardStyle}>
          <div style={smallMutedStyle}>Categoría</div>
          <div style={{ marginTop: "6px", color: "var(--text-secondary)" }}>
            {generalForm.business_name || "—"}
          </div>
        </div>

        <div style={{ ...providerCardStyle, gridColumn: "1 / -1", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={smallMutedStyle}>Estado</span>
            <StatusBadge active={active} />
          </div>
          <Button variant="secondary" onClick={toggleActive} disabled={togglingActive}>
            {togglingActive ? "Actualizando..." : active ? "Desactivar tienda" : "Activar tienda"}
          </Button>
        </div>
      </div>

      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "24px" }}>
        <TabBtn active={tab === "GENERAL"} onClick={() => setTab("GENERAL")}>
          General
        </TabBtn>
        <TabBtn active={tab === "UBICACION"} onClick={() => setTab("UBICACION")}>
          Ubicación
        </TabBtn>
        <TabBtn active={tab === "HORARIOS"} onClick={() => setTab("HORARIOS")}>
          Horarios
        </TabBtn>
        <TabBtn active={tab === "CUENTAS"} onClick={() => setTab("CUENTAS")}>
          Cuentas
        </TabBtn>
        <TabBtn active={tab === "CONTACTOS"} onClick={() => setTab("CONTACTOS")}>
          Contactos
        </TabBtn>
        <TabBtn active={tab === "LEGACY"} onClick={() => setTab("LEGACY")}>
          Legacy
        </TabBtn>
      </div>

      {tab === "GENERAL" && (
        <Card>
          <div style={{ marginBottom: "16px" }}>
            <h4 style={{ ...sectionTitleStyle, marginBottom: "6px" }}>Datos generales</h4>
            <p style={sectionTextStyle}>
              Editá la tienda, asociá una tarifa de tipo STORE y consultá el token externo
              para integraciones. El token se genera automáticamente al crear la tienda y queda
              disponible para copiar desde esta pantalla.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: "16px",
            }}
            className="edit-store-general-grid"
          >
            <div>
              <label style={{ ...smallMutedStyle, display: "block", marginBottom: "8px" }}>
                Nombre fantasía
              </label>
              <Input
                value={generalForm.name}
                onChange={(e) =>
                  setGeneralForm((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="Nombre de tienda"
              />
            </div>

            <div>
              <label style={{ ...smallMutedStyle, display: "block", marginBottom: "8px" }}>
                Categoría
              </label>
              <Input
                value={generalForm.business_name}
                onChange={(e) =>
                  setGeneralForm((prev) => ({
                    ...prev,
                    business_name: e.target.value,
                  }))
                }
                placeholder="Categoría"
              />
            </div>

            <div style={{ gridColumn: "1 / -1" }}>
              <label style={{ ...smallMutedStyle, display: "block", marginBottom: "8px" }}>
                Tarifa store
              </label>
              <select
                value={generalForm.tarifaID}
                onChange={(e) =>
                  setGeneralForm((prev) => ({ ...prev, tarifaID: e.target.value }))
                }
                style={selectStyle}
              >
                <option value="">Sin tarifa asociada</option>
                {storeTariffs.map((tariff) => (
                  <option key={tariff.id} value={String(tariff.id)}>
                    {formatTariffOptionLabel(tariff)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Input
                label="Descuento (%)"
                type="number"
                min={0}
                max={100}
                step="0.1"
                value={descuentoPct}
                onChange={(e) => setDescuentoPct(e.target.value)}
                placeholder="0"
              />
            </div>

            <div>
              <label style={{ ...smallMutedStyle, display: "block", marginBottom: "8px" }}>
                Forma de pago
              </label>
              <select
                value={formaPago}
                onChange={(e) => setFormaPago(e.target.value)}
                style={selectStyle}
              >
                <option value="">Sin definir</option>
                {FORMA_PAGO_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ marginTop: "16px", display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <Button onClick={saveGeneral} disabled={busy}>
              {busy ? "Guardando..." : "Guardar datos"}
            </Button>
          </div>

          <div style={{ ...providerCardStyle, marginTop: "20px" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "10px", color: "var(--text-primary)", fontWeight: 600 }}>
              <input
                id="store-turbo-enabled"
                type="checkbox"
                checked={turboEnabled}
                disabled={savingTurbo || store.id == null}
                onChange={(e) => saveTurboEnabled(e.target.checked)}
              />
              {savingTurbo ? "Guardando…" : "⚡ Tiene envíos Turbo"}
            </label>
            <div style={{ ...smallMutedStyle, marginTop: "6px", lineHeight: 1.5 }}>
              Se guarda al tildarla. Las tiendas marcadas son las que se muestran en Turbo · En vivo;
              no cambia el despacho. Si a una tienda sin marcar le entra un Turbo, aparece igual en
              el mapa con un «!».
            </div>
          </div>

          <div style={{ ...providerCardStyle, marginTop: "20px" }}>
            <div style={smallMutedStyle}>Token externo de integración</div>
            <div
              style={{
                marginTop: "8px",
                fontFamily: externalToken ? "monospace" : undefined,
                fontSize: externalToken ? "12px" : "14px",
                color: externalToken ? "var(--text-primary)" : "var(--text-secondary)",
                wordBreak: "break-all",
              }}
            >
              {externalToken || "Esta tienda todavía no tiene token externo visible en el frontend."}
            </div>

            <div style={{ ...smallMutedStyle, marginTop: "10px", lineHeight: 1.5 }}>
              Este token se usa como <strong>Authorization: Bearer TOKEN</strong> en los
              endpoints <strong>/v2/store/external</strong>. El token es solo lectura desde el frontend.
            </div>

            <div style={{ marginTop: "14px", display: "flex", gap: "12px", flexWrap: "wrap" }}>
              <Button variant="secondary" onClick={copyExternalToken} disabled={!externalToken}>
                Copiar token
              </Button>
            </div>
          </div>
        </Card>
      )}

      {tab === "UBICACION" && (
        <Card>
          <div style={{ marginBottom: "16px" }}>
            <h4 style={{ ...sectionTitleStyle, marginBottom: "6px" }}>Ubicación de la tienda</h4>
            <p style={sectionTextStyle}>
              Dirección y código postal operativos. Si cambian y se guardan, la geolocalización
              confirmada anteriormente queda invalidada hasta volver a verificarla.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: "16px",
            }}
            className="edit-store-general-grid"
          >
            <div style={{ gridColumn: "1 / -1" }}>
              <Input
                label="Dirección"
                value={locationForm.address}
                onChange={(e) =>
                  setLocationForm((prev) => ({ ...prev, address: e.target.value }))
                }
                placeholder="Av. Corrientes 1234"
              />
            </div>

            <Input
              label="Código postal"
              value={locationForm.postalCode}
              onChange={(e) =>
                setLocationForm((prev) => ({ ...prev, postalCode: e.target.value }))
              }
              placeholder="1414"
            />
          </div>

          <div style={{ marginTop: "16px", display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <Button onClick={saveGeneral} disabled={busy}>
              {busy ? "Guardando..." : "Guardar ubicación"}
            </Button>
          </div>

          <div style={{ ...providerCardStyle, marginTop: "20px" }}>
            <div style={smallMutedStyle}>Estado de geolocalización</div>
            <div
              role="button"
              onClick={() => setShowGeoModal(true)}
              style={{
                marginTop: "8px",
                display: "inline-block",
                cursor: "pointer",
                color: getGeoStatus(geoCoords).confirmed ? "var(--success)" : "var(--warning, #d29922)",
                fontWeight: 700,
              }}
              title="Click para buscar y confirmar la ubicación"
            >
              {getGeoStatus(geoCoords).label}
            </div>
          </div>

          {showGeoModal && store.id != null && (
            <StoreGeoLocationModal
              token={token}
              storeId={store.id}
              address={persistedAddress.address}
              postalCode={persistedAddress.postalCode}
              onClose={() => setShowGeoModal(false)}
              onConfirmed={async (coords) => {
                // Bug reportado (ver informe "LYNX — Fase 1.6, último ajuste
                // antes del commit"): confirmar acá solo actualizaba el
                // estado LOCAL del modal (geoCoords) — el array `stores` del
                // componente padre (de donde sale `store` al reabrir el
                // modal) nunca se refrescaba, así que cerrar y reabrir sin
                // recargar la página mostraba el dato viejo. onChanged()
                // dispara el mismo `load()` que ya usa saveGeneral.
                setGeoCoords(coords);
                await onChanged?.();
              }}
            />
          )}
        </Card>
      )}

      {tab === "HORARIOS" && (
        <Card>
          <div style={{ marginBottom: "16px" }}>
            <h4 style={{ ...sectionTitleStyle, marginBottom: "6px" }}>Horarios de colecta</h4>
            <p style={sectionTextStyle}>
              Lunes a viernes comparten siempre el mismo horario. Sábado y domingo se
              configuran aparte. Un grupo sin horario no se colecta esos días. Podés cargar
              más de una pasada por grupo.
            </p>
          </div>

          {scheduleLoading ? (
            <div style={{ color: "var(--text-secondary)", fontSize: "14px" }}>Cargando…</div>
          ) : (
            <>
              {scheduleInconsistent && (
                <div
                  style={{
                    marginBottom: "16px",
                    padding: "12px 14px",
                    borderRadius: "14px",
                    border: "1px solid rgba(200,144,52,0.3)",
                    background: "rgba(200,144,52,0.12)",
                    color: "var(--text-primary)",
                    fontSize: "14px",
                  }}
                >
                  Esta tienda tiene un horario cargado antes de esta regla, donde Lunes a
                  viernes no coincidían entre sí. Abajo se muestra el horario de{" "}
                  <strong>lunes</strong> como base. Si guardás, los cinco días van a quedar
                  con ese mismo horario — revisalo antes de confirmar.
                </div>
              )}

              <StoreScheduleEditor
                value={schedule}
                onChange={(next) => {
                  setSchedule(next);
                  setScheduleSaved(false);
                }}
                disabled={scheduleSaving}
              />

              <div
                style={{ marginTop: "16px", display: "flex", gap: "12px", flexWrap: "wrap" }}
              >
                <Button onClick={saveSchedule} disabled={scheduleSaving}>
                  {scheduleSaving ? "Guardando..." : "Guardar horarios"}
                </Button>
              </div>

              {scheduleSaved && (
                <div style={{ marginTop: "12px", color: "var(--success)", fontSize: "14px" }}>
                  Horarios guardados correctamente.
                </div>
              )}
            </>
          )}

          {scheduleErr && (
            <div style={{ marginTop: "12px", color: "var(--danger)", fontSize: "14px" }}>
              {scheduleErr}
            </div>
          )}
        </Card>
      )}

      {tab === "CUENTAS" && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "24px",
          }}
          className="edit-store-tabs-grid"
        >
          <Card>
            <h4 style={{ ...sectionTitleStyle, marginBottom: "14px" }}>Asignar seller</h4>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                marginBottom: "14px",
              }}
            >
              {linkedSellers.length === 0 ? (
                <div style={{ fontSize: "14px", color: "var(--text-secondary)" }}>
                  Sin sellers vinculados.
                </div>
              ) : (
                linkedSellers.map((s) => {
                  const displayName =
                    s.fullName ||
                    s.username ||
                    s.razon_social ||
                    `Seller ${s.id}`;

                  return (
                    <div
                      key={s.id}
                      style={{
                        border: "1px solid var(--border)",
                        borderRadius: "16px",
                        background: "rgba(255,255,255,0.03)",
                        padding: "12px 14px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "var(--text-primary)",
                        }}
                      >
                        {displayName}
                      </div>

                      {s.razon_social ? (
                        <div style={{ ...smallMutedStyle, marginTop: "4px" }}>
                          {s.razon_social}
                        </div>
                      ) : null}

                      <div style={{ ...smallMutedStyle, marginTop: "4px" }}>
                        Seller ID: {s.id}
                        {s.username ? ` · @${s.username}` : ""}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div style={{ marginBottom: "12px" }}>
              <Input
                value={sellerSearch}
                onChange={(e) => setSellerSearch(e.target.value)}
                placeholder="Buscar seller..."
              />
            </div>

            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              <select
                value={addingSellerId}
                onChange={(e) => setAddingSellerId(e.target.value)}
                style={{
                  flex: 1,
                  minHeight: "48px",
                  padding: "0 14px",
                  borderRadius: "16px",
                  border: "1px solid var(--border)",
                  background: "var(--surface-alt)",
                  color: "var(--text-primary)",
                }}
              >
                <option value="">Agregar seller...</option>
                {filteredSellers.map((s) => (
                  <option key={s.id} value={String(s.id)}>
                    {[
                      s.fullName || s.username || `Seller ${s.id}`,
                      s.razonSocial || "",
                    ]
                      .filter(Boolean)
                      .join(" — ")}{" "}
                    (ID {s.id})
                  </option>
                ))}
              </select>

              <Button onClick={addSeller} disabled={busy || !addingSellerId}>
                Agregar
              </Button>
            </div>

            <div style={{ ...smallMutedStyle, marginTop: "12px" }}>
              El selector prioriza nombre y apellido del usuario seller, y mantiene razón
              social como apoyo visual.
            </div>
          </Card>

          <Card>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                marginBottom: "14px",
                flexWrap: "wrap",
              }}
            >
              <h4 style={{ ...sectionTitleStyle, margin: 0 }}>Cuentas de e-commerce</h4>
              <Button variant="secondary" onClick={loadAccounts} disabled={busy}>
                Actualizar cuentas
              </Button>
            </div>

            {accounts.length === 0 ? (
              <div
                style={{
                  fontSize: "14px",
                  color: "var(--text-secondary)",
                  marginBottom: "14px",
                }}
              >
                Sin cuentas aún.
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  marginBottom: "16px",
                }}
              >
                {accounts.map((a) => {
                  const resolvedProvider = getResolvedProvider(a);
                  const linked = isMeliLinked(a);
                  const mlUserId = getAccMlUserId(a);
                  const autoEntry = getAutoEntry(a);

                  return (
                    <div
                      key={a.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        border: "1px solid var(--border)",
                        borderRadius: "16px",
                        background: "rgba(255,255,255,0.03)",
                        padding: "12px 14px",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => setSelectedAccount(a)}
                        style={{
                          flex: 1,
                          minWidth: 0,
                          background: "transparent",
                          border: "none",
                          color: "inherit",
                          textAlign: "left",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        <AccountLogo provider={resolvedProvider} />

                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "var(--text-primary)",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              flexWrap: "wrap",
                            }}
                          >
                            {getAccDisplayName(a)}
                            {linked ? (
                              <span style={{ color: "var(--success)" }}>✓ Vinculado</span>
                            ) : isMeli(resolvedProvider) ? (
                              <span style={{ color: "var(--text-secondary)" }}>• pendiente</span>
                            ) : null}
                          </div>

                          <div
                            style={{
                              ...smallMutedStyle,
                              marginTop: "4px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {isMeli(resolvedProvider) ? (
                              mlUserId != null ? (
                                <>ML user_id: {mlUserId}</>
                              ) : (
                                <>Mercado Libre sin metadata completa</>
                              )
                            ) : (
                              <>{getAccIdentifier(a)}</>
                            )}
                          </div>
                        </div>

                        <div style={{ ...smallMutedStyle, fontFamily: "monospace" }}>
                          ID {a.id}
                        </div>
                      </button>

                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          minWidth: "120px",
                          justifyContent: "flex-end",
                        }}
                      >
                        <span style={smallMutedStyle}>Auto</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleEntry(a);
                          }}
                          disabled={busy}
                          style={{
                            ...switchStyle,
                            background: autoEntry ? "var(--primary-soft)" : "var(--surface-alt)",
                          }}
                          title="Cambiar auto-entry"
                        >
                          <span
                            style={{
                              position: "absolute",
                              top: "3px",
                              left: autoEntry ? "25px" : "3px",
                              width: "22px",
                              height: "22px",
                              borderRadius: "999px",
                              background: autoEntry ? "var(--primary)" : "var(--text-secondary)",
                              transition: "all var(--transition-base)",
                            }}
                          />
                        </button>
                      </label>

                      <IconBtn
                        title="Eliminar cuenta"
                        onClick={(e) => {
                          e?.preventDefault();
                          e?.stopPropagation();
                          deleteAccount(a);
                        }}
                      >
                        🗑
                      </IconBtn>
                    </div>
                  );
                })}
              </div>
            )}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: "10px",
              }}
              className="accounts-create-grid"
            >
              <select
                value={addingAcc.provider}
                onChange={(e) =>
                  setAddingAcc((x) => ({
                    ...x,
                    provider: e.target.value as ProviderValue,
                  }))
                }
                style={selectStyle}
              >
                {PROVIDERS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>

              <Input
                value={addingAcc.accountIdentifier}
                onChange={(e) =>
                  setAddingAcc((x) => ({ ...x, accountIdentifier: e.target.value }))
                }
                placeholder="Identificador"
              />

              <Input
                value={addingAcc.displayName}
                onChange={(e) =>
                  setAddingAcc((x) => ({ ...x, displayName: e.target.value }))
                }
                placeholder="Nombre visible"
              />
            </div>

            <div style={{ ...smallMutedStyle, marginTop: "12px" }}>
              Para Mercado Libre, la cuenta se crea primero en estado pendiente y se vincula
              luego por OAuth. El cambio de auto-entry usa solo el endpoint específico.
            </div>

            <div style={{ marginTop: "14px" }}>
              <Button onClick={createAndLinkAccount} disabled={busy}>
                {busy ? "Procesando..." : "Agregar cuenta"}
              </Button>
            </div>
          </Card>
        </div>
      )}


      {tab === "CONTACTOS" && (
        <Card>
          <div style={{ marginBottom: "16px" }}>
            <h4 style={{ ...sectionTitleStyle, marginBottom: "6px" }}>Contactos del store</h4>
            <p style={sectionTextStyle}>
              Vista preparada para reflejar automáticamente los datos del o los sellers
              asignados a esta tienda.
            </p>
          </div>

          {linkedSellers.length === 0 ? (
            <div style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
              Sin sellers vinculados todavía. Cuando asignes uno o más sellers, acá vas a ver
              sus datos de contacto.
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: "16px",
              }}
              className="store-contacts-grid"
            >
              {linkedSellers.map((seller) => {
                const fullName =
                  seller.fullName ||
                  buildFullName(seller.firstName, seller.lastName) ||
                  seller.username ||
                  `Seller ${seller.id}`;

                return (
                  <div key={seller.id} style={contactCardStyle}>
                    <div>
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: 700,
                          color: "var(--text-primary)",
                        }}
                      >
                        {fullName}
                      </div>

                      {seller.razon_social ? (
                        <div style={{ ...smallMutedStyle, marginTop: "4px" }}>
                          {seller.razon_social}
                        </div>
                      ) : null}
                    </div>

                    <div style={{ display: "grid", gap: "10px" }}>
                      <div style={providerCardStyle}>
                        <div style={smallMutedStyle}>Username</div>
                        <div style={{ marginTop: "6px" }}>{seller.username || "—"}</div>
                      </div>

                      <div style={providerCardStyle}>
                        <div style={smallMutedStyle}>Email</div>
                        <div style={{ marginTop: "6px" }}>{seller.email || "—"}</div>
                      </div>

                      <div style={providerCardStyle}>
                        <div style={smallMutedStyle}>Teléfono</div>
                        <div style={{ marginTop: "6px" }}>{seller.phone || "—"}</div>
                      </div>

                      <div style={providerCardStyle}>
                        <div style={smallMutedStyle}>Seller ID</div>
                        <div style={{ marginTop: "6px", fontFamily: "monospace" }}>
                          {seller.id}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {tab === "LEGACY" && (
        <Card>
          <div style={{ marginBottom: "16px" }}>
            <h4 style={{ ...sectionTitleStyle, marginBottom: "6px" }}>Alias Legacy</h4>
            <p style={sectionTextStyle}>
              Nombres con los que este Store aparece en LightData (ej. "LOVE IS BACK", "LOVE IS
              BACK 1", "LOVE IS BACK 2" pueden ser todos alias del mismo Store). Solo afecta cómo
              se resuelven los envíos — nunca modifica nombre, tarifa ni ninguna relación Current
              del Store.
            </p>
          </div>

          {legacyErr && (
            <div style={{ marginBottom: "14px", color: "var(--danger)", fontSize: "14px" }}>
              {legacyErr}
            </div>
          )}

          <div style={{ display: "flex", gap: "10px", marginBottom: "18px", flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 260px" }}>
              <Input
                placeholder='Nuevo alias, ej. "LOVE IS BACK 3"'
                value={newAliasValue}
                onChange={(e) => setNewAliasValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !addingAlias) addLegacyAlias();
                }}
              />
            </div>
            <Button onClick={addLegacyAlias} disabled={addingAlias || !newAliasValue.trim()}>
              {addingAlias ? "Agregando…" : "Agregar alias"}
            </Button>
          </div>

          {legacyLoading ? (
            <div style={{ color: "var(--text-secondary)", fontSize: "14px" }}>Cargando alias…</div>
          ) : legacyAliases.length === 0 ? (
            <div style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
              Sin alias Legacy todavía. Los envíos de LightData con un nombre distinto al de este
              Store no se van a resolver hasta que agregues uno acá.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {legacyAliases.map((a) => (
                <div
                  key={a.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "12px",
                    padding: "10px 14px",
                    border: "1px solid var(--border)",
                    borderRadius: "12px",
                    background: "rgba(255,255,255,0.02)",
                  }}
                >
                  <span style={{ fontFamily: "monospace", fontSize: "13px" }}>{a.legacy_nombre_fantasia}</span>
                  <button
                    type="button"
                    onClick={() => removeLegacyAlias(a.id, a.legacy_nombre_fantasia)}
                    disabled={removingAliasId === a.id}
                    style={{
                      background: "transparent",
                      border: "1px solid var(--border)",
                      borderRadius: "8px",
                      padding: "4px 10px",
                      color: "var(--danger, #d1242f)",
                      cursor: "pointer",
                      fontSize: "12px",
                    }}
                  >
                    {removingAliasId === a.id ? "Eliminando…" : "Eliminar"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {err && (
        <div style={{ marginTop: "16px", color: "var(--danger)", fontSize: "14px" }}>
          {err}
        </div>
      )}

      <div style={{ marginTop: "24px", textAlign: "right" }}>
        <Button variant="ghost" onClick={onClose}>
          Cerrar
        </Button>
      </div>

      {selectedAccount && (
        <AccountDetailModal
          storeId={store.id ?? 0}
          account={selectedAccount}
          token={token}
          onClose={() => setSelectedAccount(null)}
          onUpdated={async () => {
            await loadAccounts();
            await onChanged?.();
          }}
          // El listado de Tiendas no muestra nada de la vinculación: alcanza
          // con las cuentas de esta tienda.
          onLinked={loadAccounts}
        />
      )}

      <style>
        {`
          @media (max-width: 980px) {
            .edit-store-head-grid {
              grid-template-columns: 1fr !important;
            }

            .edit-store-tabs-grid {
              grid-template-columns: 1fr !important;
            }

            .accounts-create-grid {
              grid-template-columns: 1fr !important;
            }

            .store-contacts-grid,
            .edit-store-general-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>
    </Modal>
  );
}

/* ================== Modal detalle de account ================== */
// Exportado para las pruebas (StoresAccountLink.test.tsx).
export function AccountDetailModal({
  storeId,
  account,
  token,
  onClose,
  onUpdated,
  onLinked,
}: AccountDetailModalProps) {
  const resolvedProvider = getResolvedProvider(account);
  const linked = isMeliLinked(account);
  const tokenMlUserId = account.__tokenMlUserId ?? null;
  const currentMlUserId = getAccMlUserId(account);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  const [displayName, setDisplayName] = useState(
    account.display_name || account.displayName || ""
  );
  const [provider, setProvider] = useState(
    isTokenMeliCandidate({
      linked: account.__hasOAuthToken === true,
      user_id: tokenMlUserId,
    })
      ? "Mercadolibre"
      : resolvedProvider || ""
  );
  const [mlUserId, setMlUserId] = useState<string>(
    currentMlUserId != null ? String(currentMlUserId) : tokenMlUserId != null ? String(tokenMlUserId) : ""
  );
  const [autoEntry, setAutoEntry] = useState(getAutoEntry(account));

  const providerLocked = tokenMlUserId != null || account.__hasOAuthToken === true;

  // El chequeo del «Link de vinculación»: uno solo por ventana. Pedir otro
  // link corta el anterior y cerrar la ventana lo corta (antes seguía
  // corriendo y se apilaba con el siguiente).
  const linkPollRef = useRef<number | null>(null);
  const stopLinkPoll = useCallback(() => {
    if (linkPollRef.current != null) window.clearInterval(linkPollRef.current);
    linkPollRef.current = null;
  }, []);
  useEffect(() => stopLinkPoll, [stopLinkPoll]);

  useEffect(() => {
    setDisplayName(account.display_name || account.displayName || "");
    setProvider(
      tokenMlUserId != null || account.__hasOAuthToken === true
        ? "Mercadolibre"
        : resolvedProvider || ""
    );
    setMlUserId(
      currentMlUserId != null
        ? String(currentMlUserId)
        : tokenMlUserId != null
        ? String(tokenMlUserId)
        : ""
    );
    setAutoEntry(getAutoEntry(account));
  }, [account, resolvedProvider, tokenMlUserId, currentMlUserId]);

  const copyLink = async () => {
    setBusy(true);
    setErr(null);
    setOkMsg(null);

    try {
      const url = await requestMercadoLibreLink({
        token,
        accountId: account.id,
      });

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        window.prompt("Copiá el link manualmente:", url);
      }

      setOkMsg(
        "Link copiado. Cuando el cliente acepte la vinculación, la cuenta se actualizará automáticamente."
      );

      stopLinkPoll();
      let attempts = 0;
      let checking = false;
      const maxAttempts = 30;

      // Cada vuelta solo pregunta si la cuenta ya quedó vinculada. Antes
      // llamaba también a onUpdated, que recarga las cuentas Y el listado
      // completo de Tiendas: con el listado en «Cargando…» cada 3 segundos,
      // las tiendas aparecían y desaparecían durante 90 segundos.
      const interval = window.setInterval(async () => {
        if (checking) return;
        checking = true;
        attempts += 1;

        try {
          const tokenResp = await authFetch(`/v2/tokens/${account.id}`, { token });
          const tokenJson: TokensResponse = await tokenResp.json().catch(() => ({}));
          // Mientras esperaba la respuesta se cerró la ventana o se pidió otro link.
          if (linkPollRef.current !== interval) return;
          const tokenRecord = pickTokenRecord(tokenJson);

          if (tokenResp.ok && tokenJson?.ok !== false && hasValidMlToken(tokenRecord)) {
            stopLinkPoll();
            setOkMsg("Cuenta de Mercado Libre vinculada correctamente.");

            // Se refresca una sola vez, al final.
            setTimeout(async () => {
              await (onLinked ?? onUpdated)?.();
              onClose();
            }, 900);
            return;
          }
        } catch {
          // La vinculación puede tardar. Seguimos intentando hasta maxAttempts.
        } finally {
          checking = false;
        }

        if (attempts >= maxAttempts && linkPollRef.current === interval) {
          stopLinkPoll();
          setOkMsg(
            "Link copiado. Si el cliente ya aceptó, cerrá y volvé a abrir la cuenta para ver los datos actualizados."
          );
        }
      }, 3000);
      linkPollRef.current = interval;
    } catch (e) {
      console.error("ML copyLink error:", e);
      setErr(e instanceof Error ? e.message : "Error copiando el link");
    } finally {
      setBusy(false);
    }
  };

  const handleToggleEntry = async () => {
    setBusy(true);
    setErr(null);
    setOkMsg(null);

    try {
      const next = !autoEntry;
      await updateAccountEntryOnly(token, Number(account.id), next);
      setAutoEntry(next);
      setOkMsg(`Auto-entry ${next ? "activado" : "desactivado"}.`);
      await onUpdated?.();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error actualizando auto-entry");
    } finally {
      setBusy(false);
    }
  };

  const handleRecoverAccount = async () => {
    setBusy(true);
    setErr(null);
    setOkMsg(null);

    try {
      const normalizedProvider =
        providerLocked || tokenMlUserId != null ? "Mercadolibre" : provider.trim();

      const normalizedMlUserId =
        mlUserId.trim() !== ""
          ? Number(mlUserId)
          : tokenMlUserId != null
          ? Number(tokenMlUserId)
          : null;

      if (!normalizedProvider) {
        throw new Error("Completá el provider.");
      }

      if (normProvider(normalizedProvider) === "mercadolibre" && !normalizedMlUserId) {
        throw new Error("Completá el ml_user_id para la cuenta de Mercado Libre.");
      }

      await updateAccountSafely(token, {
        id: Number(account.id),
        provider: normalizedProvider,
        display_name: displayName.trim() || "Pendiente",
        ml_user_id: normalizedMlUserId,
        auto_entry: autoEntry,
      });

      setOkMsg("Cuenta actualizada correctamente.");
      await onUpdated?.();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error actualizando la cuenta");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
          <AccountLogo provider={provider || resolvedProvider} />
          <span>Cuenta</span>
          <span style={{ color: "var(--text-secondary)" }}>
            — {displayName || getAccDisplayName(account)}
          </span>
          <span style={{ marginLeft: "8px", color: linked ? "var(--success)" : "var(--danger)" }}>
            {linked ? "✓ Vinculado" : "✗ No vinculado"}
          </span>
        </div>
      }
      onClose={onClose}
      width="980px"
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "12px",
          marginBottom: "16px",
        }}
        className="account-detail-grid"
      >
        <div style={providerCardStyle}>
          <div style={smallMutedStyle}>Provider</div>
          {providerLocked ? (
            <div style={{ marginTop: "6px", textTransform: "capitalize", fontWeight: 700 }}>
              Mercado Libre
            </div>
          ) : (
            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              style={{ ...selectStyle, marginTop: "8px" }}
            >
              <option value="">Seleccionar</option>
              <option value="Mercadolibre">Mercado Libre</option>
              <option value="Tiendanube">Tienda Nube</option>
              <option value="Shopify">Shopify</option>
              <option value="WooCommerce">WooCommerce</option>
              <option value="Otros">Otros</option>
            </select>
          )}
          {providerLocked ? (
            <div style={{ ...smallMutedStyle, marginTop: "6px" }}>
              Provider bloqueado porque hay token ML asociado.
            </div>
          ) : null}
        </div>

        <div style={providerCardStyle}>
          <div style={smallMutedStyle}>Account ID (interno)</div>
          <div style={{ marginTop: "6px", fontFamily: "monospace" }}>{account.id}</div>
        </div>

        <div style={providerCardStyle}>
          <div style={smallMutedStyle}>Nombre visible</div>
          <Input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Nombre visible"
          />
        </div>

        <div style={providerCardStyle}>
          <div style={smallMutedStyle}>ML user_id</div>
          <Input
            value={mlUserId}
            onChange={(e) => setMlUserId(e.target.value)}
            placeholder="ML user_id"
            disabled={tokenMlUserId != null}
          />
          {tokenMlUserId != null ? (
            <div style={{ ...smallMutedStyle, marginTop: "6px" }}>
              Detectado desde tokens: {tokenMlUserId}
            </div>
          ) : null}
        </div>

        <div style={{ ...providerCardStyle, gridColumn: "1 / -1" }}>
          <div style={smallMutedStyle}>Identificador</div>
          <div style={{ marginTop: "6px", fontFamily: "monospace" }}>
            {isMeli(provider || resolvedProvider)
              ? mlUserId || "— (pendiente de completar)"
              : getAccIdentifier(account)}
          </div>
          <div style={{ ...smallMutedStyle, marginTop: "6px" }}>
            ML: se completa al finalizar OAuth o se recupera desde tokens · TN: shop id/url ·
            Shopify: domain · WooCommerce: url
          </div>
        </div>

        {isMeli(provider || resolvedProvider) && (
          <div style={{ ...providerCardStyle, gridColumn: "1 / -1" }}>
            <div style={smallMutedStyle}>Estado OAuth</div>
            <div style={{ marginTop: "6px", fontFamily: "monospace" }}>
              {linked
                ? "Vinculada con token activo"
                : account.__hasOAuthToken
                ? "Hay token ML, falta reconstruir metadata"
                : "Pendiente de vinculación"}
            </div>
          </div>
        )}

        <div style={{ ...providerCardStyle, gridColumn: "1 / -1" }}>
          <div style={smallMutedStyle}>Auto-entry</div>
          <div
            style={{
              marginTop: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "16px",
              flexWrap: "wrap",
            }}
          >
            <div>
              <div style={{ fontWeight: 700, color: "var(--text-primary)" }}>
                {autoEntry ? "Activo" : "Inactivo"}
              </div>
              <div style={{ ...smallMutedStyle, marginTop: "4px" }}>
                Este cambio usa exclusivamente el endpoint específico de entry.
              </div>
            </div>

            <button
              type="button"
              onClick={handleToggleEntry}
              disabled={busy}
              style={{
                ...switchStyle,
                background: autoEntry ? "var(--primary-soft)" : "var(--surface-alt)",
              }}
              title="Cambiar auto-entry"
            >
              <span
                style={{
                  position: "absolute",
                  top: "3px",
                  left: autoEntry ? "25px" : "3px",
                  width: "22px",
                  height: "22px",
                  borderRadius: "999px",
                  background: autoEntry ? "var(--primary)" : "var(--text-secondary)",
                  transition: "all var(--transition-base)",
                }}
              />
            </button>
          </div>
        </div>
      </div>

      {isMeli(provider || resolvedProvider) && (
        <Card>
          <div style={{ color: "var(--text-secondary)", fontSize: "14px", marginBottom: "14px" }}>
            Vinculación Mercado Libre vía OAuth. Copiá el link y envialo al cliente. Cuando
            el cliente acepte, backend guarda provider, display_name y ml_user_id automáticamente.
          </div>

          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <Button variant="secondary" onClick={copyLink} disabled={busy}>
              {busy ? "Generando..." : "Link de vinculación"}
            </Button>
          </div>
        </Card>
      )}

      {!isMeli(provider || resolvedProvider) && (
        <div style={{ textAlign: "right" }}>
          <Button onClick={handleRecoverAccount} disabled={busy}>
            {busy ? "Guardando..." : "Guardar cuenta"}
          </Button>
        </div>
      )}

      {err && (
        <div style={{ marginTop: "14px", color: "var(--danger)", fontSize: "14px" }}>
          {err}
        </div>
      )}
      {okMsg && (
        <div style={{ marginTop: "14px", color: "var(--success)", fontSize: "14px" }}>
          {okMsg}
        </div>
      )}

      <div style={{ marginTop: "20px", textAlign: "right" }}>
        <Button variant="ghost" onClick={onClose}>
          Cerrar
        </Button>
      </div>

      <style>
        {`
          @media (max-width: 720px) {
            .account-detail-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>
    </Modal>
  );
}